import React, { useState, useEffect, useCallback, useRef } from 'react';
import { landingAdminService, BannerRow } from '../../services/publicMarketService';
import { MAJOR_COINS_CHART_OPTIONS, normalizeMajorCoinsChartType } from '../../constants/majorCoinsChartTypes';
import { useAppSelector } from '../../store/hooks';
import { useNavigate } from 'react-router-dom';

interface LandingConfig {
  published: boolean;
  maskAllSections: boolean;
  /** Public /market: blue strip with “Public view…” / Refresh (default true). */
  showPublicRefreshBanner: boolean;
  heroTitle: string;
  heroSubtitle: string;
  heroCtaPrimary: string;
  heroCtaSecondary: string;
  heroCtaPrimaryUrl: string;
  heroCtaSecondaryUrl: string;
  showCtaPrimary: boolean;
  showCtaSecondary: boolean;
  navBrandText: string;
  navTagline: string;
  navBtn1Label: string;
  navBtn1Url: string;
  navBtn1Visible: boolean;
  navBtn2Label: string;
  navBtn2Url: string;
  navBtn2Visible: boolean;
  navBtn3Label: string;
  navBtn3Url: string;
  navBtn3Visible: boolean;
  showOracleFeed: boolean;
  showBtcFilter: boolean;
  maskBtcRegime: boolean;
  showMicroReversalFilter: boolean;
  maskMicroReversal: boolean;
  showParticipation: boolean;
  maskParticipation: boolean;
  showSectorBreakdown: boolean;
  showAiRegime: boolean;
  maskAiRegime: boolean;
  showMajorCoinsMarket: boolean;
  maskMajorCoinsMarket: boolean;
  showMajorCoinsChartStyleOptions: boolean;
  majorCoinsDefaultChart: string;
  showSignalCardOracle: boolean;
  signalCardOracleIcon: string;
  signalCardOracleTitle: string;
  signalCardOracleDesc: string;
  showSignalCardBtcRegime: boolean;
  signalCardBtcRegimeIcon: string;
  signalCardBtcRegimeTitle: string;
  signalCardBtcRegimeDesc: string;
  showSignalCardMicroReversal: boolean;
  signalCardMicroReversalIcon: string;
  signalCardMicroReversalTitle: string;
  signalCardMicroReversalDesc: string;
  showSignalCardParticipation: boolean;
  signalCardParticipationIcon: string;
  signalCardParticipationTitle: string;
  signalCardParticipationDesc: string;
  showSignalCardRisk: boolean;
  signalCardRiskIcon: string;
  signalCardRiskTitle: string;
  signalCardRiskDesc: string;
  oracleFeedLimit: number;
  announcement: string | null;
  // Footer
  footerCopyright: string;
  footerLink1Label: string;
  footerLink1Url: string;
  footerLink1Visible: boolean;
  footerLink2Label: string;
  footerLink2Url: string;
  footerLink2Visible: boolean;
  footerLink3Label: string;
  footerLink3Url: string;
  footerLink3Visible: boolean;
  footerLink4Label: string;
  footerLink4Url: string;
  footerLink4Visible: boolean;
  // Bottom CTA banner
  ctaBannerTitle: string;
  ctaBannerSubtitle: string;
  // Proof sections
  showProofRegime: boolean;
  proofRegimeTitle: string;
  proofRegimeSubtitle: string;
  proofRegimeAccuracy: string;
  proofRegimeRows: string;
  showProofParticipation: boolean;
  proofParticipationTitle: string;
  proofParticipationSubtitle: string;
  proofHotAvg: string;
  proofCoolAvg: string;
  proofParticipationRows: string;
  showProofSignals: boolean;
  proofSignalsTitle: string;
  proofSignalsSubtitle: string;
  proofSignalWinRate: string;
  proofSignalAvgMove: string;
  proofSignalMaxDD: string;
  proofSignalRows: string;
  showAboutSection: boolean;
  maskAboutSection: boolean;
  aboutBadge: string;
  aboutTitle: string;
  aboutBody: string;
  showProofWinningTrades: boolean;
  proofWinningTradesTitle: string;
  proofWinningTradesSubtitle: string;
  showProofWinningTradesStrategyName: boolean;
  showProofLosingTrades: boolean;
  showProofWinningTradesDailyPnl: boolean;
  showProofWinningTradesDailyPnlPerStrategy: boolean;
  maskWinningTrades: boolean;
  winningTradesDisplayLimit: number;
  // Paid banners
  bannerL1Visible: boolean; bannerL1Masked: boolean; bannerL1Badge?: string; bannerL1Title?: string; bannerL1Subtitle?: string; bannerL1ImageUrl?: string; bannerL1LinkUrl?: string; bannerL1LinkLabel?: string; bannerL1BgColor?: string;
  bannerL2Visible: boolean; bannerL2Masked: boolean; bannerL2Badge?: string; bannerL2Title?: string; bannerL2Subtitle?: string; bannerL2ImageUrl?: string; bannerL2LinkUrl?: string; bannerL2LinkLabel?: string; bannerL2BgColor?: string;
  bannerL3Visible: boolean; bannerL3Masked: boolean; bannerL3Badge?: string; bannerL3Title?: string; bannerL3Subtitle?: string; bannerL3ImageUrl?: string; bannerL3LinkUrl?: string; bannerL3LinkLabel?: string; bannerL3BgColor?: string;
  bannerR1Visible: boolean; bannerR1Masked: boolean; bannerR1Badge?: string; bannerR1Title?: string; bannerR1Subtitle?: string; bannerR1ImageUrl?: string; bannerR1LinkUrl?: string; bannerR1LinkLabel?: string; bannerR1BgColor?: string;
  bannerR2Visible: boolean; bannerR2Masked: boolean; bannerR2Badge?: string; bannerR2Title?: string; bannerR2Subtitle?: string; bannerR2ImageUrl?: string; bannerR2LinkUrl?: string; bannerR2LinkLabel?: string; bannerR2BgColor?: string;
  bannerR3Visible: boolean; bannerR3Masked: boolean; bannerR3Badge?: string; bannerR3Title?: string; bannerR3Subtitle?: string; bannerR3ImageUrl?: string; bannerR3LinkUrl?: string; bannerR3LinkLabel?: string; bannerR3BgColor?: string;
  updatedBy?: string;
  updatedAt?: string;
}

// ── tiny helpers ──────────────────────────────────────────────────────────────

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{
    background: '#fff', border: '1px solid #e2e8f0',
    borderRadius: 14, padding: '20px 24px', marginBottom: 20, ...style,
  }}>
    {children}
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode; badge?: React.ReactNode }> = ({ children, badge }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
    <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{children}</span>
    {badge}
  </div>
);

const Toggle: React.FC<{ checked: boolean; onChange: () => void; disabled?: boolean }> = ({ checked, onChange, disabled }) => (
  <div
    onClick={() => !disabled && onChange()}
    style={{
      width: 40, height: 22, borderRadius: 11, position: 'relative',
      background: checked ? '#2563eb' : '#cbd5e1',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background 0.2s', flexShrink: 0, opacity: disabled ? 0.5 : 1,
    }}
  >
    <div style={{
      position: 'absolute', width: 16, height: 16, borderRadius: '50%', background: '#fff',
      top: 3, left: checked ? 21 : 3, transition: 'left 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }} />
  </div>
);

const FieldInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
  multiline?: boolean;
  placeholder?: string;
  hint?: string;
}> = ({ label, value, onChange, disabled, multiline, placeholder, hint }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ fontSize: 12, fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 5 }}>
      {label}
    </label>
    {multiline ? (
      <textarea
        rows={3}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '9px 12px', border: `1.5px solid ${disabled ? '#e2e8f0' : '#93c5fd'}`,
          borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
          color: '#1e293b', background: disabled ? '#f8fafc' : '#fff',
          outline: 'none', transition: 'border-color 0.15s',
        }}
      />
    ) : (
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        style={{
          width: '100%', padding: '9px 12px', border: `1.5px solid ${disabled ? '#e2e8f0' : '#93c5fd'}`,
          borderRadius: 8, fontSize: 13, boxSizing: 'border-box',
          color: '#1e293b', background: disabled ? '#f8fafc' : '#fff',
          outline: 'none', transition: 'border-color 0.15s',
        }}
      />
    )}
    {hint && <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>{hint}</p>}
  </div>
);

// ── BannerImageDropZone ────────────────────────────────────────────────────────

interface BannerImageDropZoneProps {
  value: string;
  disabled?: boolean;
  onChange: (url: string) => void;
}

const BannerImageDropZone: React.FC<BannerImageDropZoneProps> = ({ value, disabled, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  const applyFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    setUploadError(null);

    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = ev => setLocalPreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    // Upload the file to the server, get back a hosted URL
    setUploading(true);
    landingAdminService.uploadBannerImage(file)
      .then(url => {
        setLocalPreview(null);
        onChange(url);
      })
      .catch((err: Error) => {
        setLocalPreview(null);
        setUploadError(
          err.message === '404'
            ? 'Image upload not supported by the server. Please use "Paste URL" to enter an image URL instead.'
            : `Upload failed (${err.message}). Please use "Paste URL" to enter an image URL instead.`
        );
      })
      .finally(() => setUploading(false));
  }, [onChange]);

  const applyUrl = () => {
    if (urlInput.trim()) {
      onChange(urlInput.trim());
      setUrlInput('');
      setShowUrlInput(false);
    }
  };

  return (
    <div>
      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault();
          setDragOver(false);
          if (disabled) return;
          const file = e.dataTransfer.files[0];
          if (file) applyFile(file);
        }}
        onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? '#086afb' : (value || localPreview) ? '#086afb40' : '#cbd5e1'}`,
          borderRadius: 10,
          padding: (value || localPreview) ? '8px' : '20px 12px',
          textAlign: 'center',
          cursor: disabled || uploading ? 'default' : 'pointer',
          background: dragOver ? 'rgba(8,106,251,0.06)' : (value || localPreview) ? 'rgba(8,106,251,0.02)' : '#fafbfc',
          transition: 'border-color 0.15s, background 0.15s',
          position: 'relative',
        }}
      >
        {uploading && (
          <div style={{ position: 'absolute', inset: 0, borderRadius: 8, background: 'rgba(255,255,255,0.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
            <svg style={{ width: 24, height: 24, animation: 'spin 1s linear infinite', color: '#086afb' }} viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
              <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <div style={{ fontSize: 11, color: '#086afb', fontWeight: 600, marginTop: 6 }}>Uploading…</div>
          </div>
        )}
        {(localPreview || value) ? (
          <>
            <img
              src={localPreview || value}
              alt="banner preview"
              style={{ width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 7, display: 'block', marginBottom: 5, opacity: uploading ? 0.4 : 1 }}
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, letterSpacing: '0.05em', userSelect: 'none' }}>
              DROP NEW IMAGE TO REPLACE · OR CLICK TO BROWSE
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 26, marginBottom: 4, userSelect: 'none' }}>🖼️</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: dragOver ? '#086afb' : '#475569', userSelect: 'none' }}>
              {dragOver ? 'Drop to upload' : 'Drag & drop image here'}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, userSelect: 'none' }}>
              or click to browse · PNG, JPG, WebP, GIF
            </div>
          </>
        )}
      </div>

      {/* Upload error */}
      {uploadError && (
        <div style={{ marginTop: 6, padding: '8px 10px', background: '#fff1f2', border: '1px solid #fecaca', borderRadius: 7, fontSize: 11, color: '#b91c1c', lineHeight: 1.4 }}>
          {uploadError}
          <button onClick={() => setUploadError(null)} style={{ float: 'right', background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontWeight: 700, padding: 0 }}>✕</button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={e => { const file = e.target.files?.[0]; if (file) applyFile(file); e.target.value = ''; }}
      />

      {/* Action row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        {!disabled && !showUrlInput && (
          <button
            onClick={e => { e.stopPropagation(); setShowUrlInput(true); }}
            style={{ fontSize: 11, color: '#086afb', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}
          >
            + Paste URL instead
          </button>
        )}
        {value && !disabled && !uploading && (
          <button
            onClick={e => { e.stopPropagation(); onChange(''); }}
            style={{ fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600, marginLeft: 'auto' }}
          >
            ✕ Remove
          </button>
        )}
      </div>

      {/* URL paste input */}
      {showUrlInput && !disabled && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input
            autoFocus
            type="text"
            value={urlInput}
            placeholder="https://… or /assets/banners/banner-hero-1200x400.png"
            onChange={e => setUrlInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') applyUrl(); if (e.key === 'Escape') { setShowUrlInput(false); setUrlInput(''); } }}
            style={{
              flex: 1, padding: '6px 9px', borderRadius: 7,
              border: '1px solid #086afb', fontSize: 12, color: '#1e293b',
            }}
          />
          <button
            onClick={applyUrl}
            style={{ padding: '6px 12px', borderRadius: 7, background: '#086afb', color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
          >
            Apply
          </button>
          <button
            onClick={() => { setShowUrlInput(false); setUrlInput(''); }}
            style={{ padding: '6px 10px', borderRadius: 7, background: '#f1f5f9', color: '#64748b', border: 'none', fontSize: 12, cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

// ── main component ────────────────────────────────────────────────────────────

const AdminLandingPageManager: React.FC = () => {
  const navigate = useNavigate();
  const adminUser = useAppSelector((s: any) => s.auth.adminUser);

  // SUPER_ADMIN always gets full access; otherwise check explicit permissions
  // permissions may be a string[] or a comma-separated string (legacy persist)
  const rawPerms: string[] = Array.isArray(adminUser?.permissions)
    ? adminUser.permissions
    : typeof adminUser?.permissions === 'string'
      ? (adminUser.permissions as string).split(',').map((p: string) => p.trim()).filter(Boolean)
      : [];
  const isSuperAdmin = adminUser?.role === 'SUPER_ADMIN';
  const canManage = isSuperAdmin || rawPerms.includes('MANAGE_LANDING_PAGE');
  const canView   = canManage || rawPerms.includes('VIEW_LANDING_PAGE');

  // Debug — remove after confirming fix
  console.log('[AdminLandingPageManager] adminUser role:', adminUser?.role, 'permissions:', rawPerms, 'canManage:', canManage);

  const [config, setConfig] = useState<LandingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  // ── Dynamic banners state ─────────────────────────────────────────────────
  const [banners, setBanners] = useState<BannerRow[]>([]);
  const [bannerSaving, setBannerSaving] = useState<number | 'new' | null>(null);

  // Separate edit forms for each section
  const [heroEditing, setHeroEditing] = useState(false);
  const [heroForm, setHeroForm] = useState({ heroTitle: '', heroSubtitle: '', heroCtaPrimary: '', heroCtaSecondary: '', heroCtaPrimaryUrl: '', heroCtaSecondaryUrl: '', announcement: '' });

  const [navEditing, setNavEditing] = useState(false);
  const [navForm, setNavForm] = useState({
    navBrandText: '',
    navTagline: '',
    navBtn1Label: '',
    navBtn1Url: '',
    navBtn2Label: '',
    navBtn2Url: '',
    navBtn3Label: '',
    navBtn3Url: ''
  });

  const [ctaBannerEditing, setCtaBannerEditing] = useState(false);
  const [ctaBannerForm, setCtaBannerForm] = useState({ ctaBannerTitle: '', ctaBannerSubtitle: '' });

  const [signalCardsEditing, setSignalCardsEditing] = useState(false);
  const [signalCardsForm, setSignalCardsForm] = useState({
    signalCardOracleIcon: '', signalCardOracleTitle: '', signalCardOracleDesc: '',
    signalCardBtcRegimeIcon: '', signalCardBtcRegimeTitle: '', signalCardBtcRegimeDesc: '',
    signalCardParticipationIcon: '', signalCardParticipationTitle: '', signalCardParticipationDesc: '',
    signalCardRiskIcon: '', signalCardRiskTitle: '', signalCardRiskDesc: '',
  });

  const [footerEditing, setFooterEditing] = useState(false);
  const [footerForm, setFooterForm] = useState({
    footerCopyright: '',
    footerLink1Label: '', footerLink1Url: '',
    footerLink2Label: '', footerLink2Url: '',
    footerLink3Label: '', footerLink3Url: '',
    footerLink4Label: '', footerLink4Url: '',
  });

  const [aboutEditing, setAboutEditing] = useState(false);
  const [aboutForm, setAboutForm] = useState({
    aboutBadge: '', aboutTitle: '', aboutBody: '',
    aboutHighlight1Icon: '', aboutHighlight1Title: '', aboutHighlight1Text: '',
    aboutHighlight2Icon: '', aboutHighlight2Title: '', aboutHighlight2Text: '',
    aboutHighlight3Icon: '', aboutHighlight3Title: '', aboutHighlight3Text: '',
  });

  const [proofRegimeEditing, setProofRegimeEditing] = useState(false);
  const [proofRegimeForm, setProofRegimeForm] = useState({
    proofRegimeTitle: '', proofRegimeSubtitle: '', proofRegimeAccuracy: '', proofRegimeRows: '',
  });

  const [proofParticipationEditing, setProofParticipationEditing] = useState(false);
  const [proofParticipationForm, setProofParticipationForm] = useState({
    proofParticipationTitle: '', proofParticipationSubtitle: '',
    proofHotAvg: '', proofCoolAvg: '', proofParticipationRows: '',
  });

  const [proofSignalsEditing, setProofSignalsEditing] = useState(false);
  const [proofSignalsForm, setProofSignalsForm] = useState({
    proofSignalsTitle: '', proofSignalsSubtitle: '',
    proofSignalWinRate: '', proofSignalAvgMove: '', proofSignalMaxDD: '', proofSignalRows: '',
  });

  const [proofWinningTradesEditing, setProofWinningTradesEditing] = useState(false);
  const [proofWinningTradesForm, setProofWinningTradesForm] = useState({
    proofWinningTradesTitle: '', proofWinningTradesSubtitle: '',
    showProofWinningTradesStrategyName: true,
  });

  const showToast = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      console.log('AdminLandingPageManager: loading config, canManage=', canManage, 'canView=', canView);
      const data = await landingAdminService.getConfig();
      console.log('AdminLandingPageManager: config loaded', data);
      setConfig({
        ...(data as LandingConfig),
        showPublicRefreshBanner: (data as any).showPublicRefreshBanner ?? true,
      });
      setHeroForm({
        heroTitle: data.heroTitle ?? '',
        heroSubtitle: data.heroSubtitle ?? '',
        heroCtaPrimary: data.heroCtaPrimary ?? '',
        heroCtaSecondary: data.heroCtaSecondary ?? '',
        heroCtaPrimaryUrl: data.heroCtaPrimaryUrl ?? '/register',
        heroCtaSecondaryUrl: data.heroCtaSecondaryUrl ?? '/login',
        announcement: data.announcement ?? '',
      });
      setNavForm({
        navBrandText: data.navBrandText ?? 'Zogzila Client',
        navTagline: data.navTagline ?? 'MARKET SIGNALS',
        navBtn1Label: data.navBtn1Label ?? 'Login',
        navBtn1Url: data.navBtn1Url ?? '/login',
        navBtn2Label: data.navBtn2Label ?? 'Register',
        navBtn2Url: data.navBtn2Url ?? '/register/personal',
        navBtn3Label: data.footerLink4Label ?? 'Business Register',
        navBtn3Url: data.footerLink4Url ?? '/register/business',
      });
      setCtaBannerForm({
        ctaBannerTitle:    data.ctaBannerTitle    ?? 'Ready to Trade with an Edge?',
        ctaBannerSubtitle: data.ctaBannerSubtitle ?? 'Join our trading platform to access automated strategies, personalised signal feeds, and real-time portfolio management.',
      });
      setSignalCardsForm({
        signalCardOracleIcon:         (data as any).signalCardOracleIcon         ?? '🔮',
        signalCardOracleTitle:        (data as any).signalCardOracleTitle        ?? 'Oracle Rankings',
        signalCardOracleDesc:         (data as any).signalCardOracleDesc         ?? 'AI-scored coin rankings updated in real-time, giving you the highest-conviction LONG and SHORT candidates.',
        signalCardBtcRegimeIcon:      (data as any).signalCardBtcRegimeIcon      ?? '📊',
        signalCardBtcRegimeTitle:     (data as any).signalCardBtcRegimeTitle     ?? 'BTC Regime Detection',
        signalCardBtcRegimeDesc:      (data as any).signalCardBtcRegimeDesc      ?? 'Understand whether the macro crypto market is bullish, bearish, or ranging before placing any trade.',
        signalCardParticipationIcon:  (data as any).signalCardParticipationIcon  ?? '🔥',
        signalCardParticipationTitle: (data as any).signalCardParticipationTitle ?? 'Participation Filter',
        signalCardParticipationDesc:  (data as any).signalCardParticipationDesc  ?? 'Know which coins are actually moving with volume behind them — not just random price noise.',
        signalCardRiskIcon:           (data as any).signalCardRiskIcon           ?? '🛡️',
        signalCardRiskTitle:          (data as any).signalCardRiskTitle          ?? 'Risk-Aware Signals',
        signalCardRiskDesc:           (data as any).signalCardRiskDesc           ?? 'RSI, EMA, funding rate and momentum all combined so you never trade against the current.',
      });
      setFooterForm({
        footerCopyright: data.footerCopyright ?? '',
        footerLink1Label: data.footerLink1Label ?? 'Login',
        footerLink1Url:   data.footerLink1Url   ?? '/login',
        footerLink2Label: data.footerLink2Label ?? 'Register',
        footerLink2Url:   data.footerLink2Url   ?? '/register',
        footerLink3Label: data.footerLink3Label ?? 'Personal',
        footerLink3Url:   data.footerLink3Url   ?? '/register/personal',
        footerLink4Label: data.footerLink4Label ?? 'Business',
        footerLink4Url:   data.footerLink4Url   ?? '/business/register',
      });
      setAboutForm({
        aboutBadge:           (data as any).aboutBadge           ?? 'About Zogzila',
        aboutTitle:           (data as any).aboutTitle           ?? 'What is Zogzila?',
        aboutBody:            (data as any).aboutBody            ?? '',
        aboutHighlight1Icon:  (data as any).aboutHighlight1Icon  ?? '📡',
        aboutHighlight1Title: (data as any).aboutHighlight1Title ?? 'Real-Time Signals',
        aboutHighlight1Text:  (data as any).aboutHighlight1Text  ?? '',
        aboutHighlight2Icon:  (data as any).aboutHighlight2Icon  ?? '🧠',
        aboutHighlight2Title: (data as any).aboutHighlight2Title ?? 'AI-Powered Analysis',
        aboutHighlight2Text:  (data as any).aboutHighlight2Text  ?? '',
        aboutHighlight3Icon:  (data as any).aboutHighlight3Icon  ?? '🏆',
        aboutHighlight3Title: (data as any).aboutHighlight3Title ?? 'Proven Results',
        aboutHighlight3Text:  (data as any).aboutHighlight3Text  ?? '',
      });
      setProofRegimeForm({
        proofRegimeTitle:    (data as any).proofRegimeTitle    ?? 'Market Condition Accuracy',
        proofRegimeSubtitle: (data as any).proofRegimeSubtitle ?? 'Track when our system calls Trending vs Ranging and what actually happens next',
        proofRegimeAccuracy: (data as any).proofRegimeAccuracy ?? '',
        proofRegimeRows:     (data as any).proofRegimeRows     ?? '',
      });
      setProofParticipationForm({
        proofParticipationTitle:    (data as any).proofParticipationTitle    ?? 'Participation Filter Effectiveness',
        proofParticipationSubtitle: (data as any).proofParticipationSubtitle ?? 'HOT coins vs COOL coins — where real momentum lives',
        proofHotAvg:                (data as any).proofHotAvg                ?? '',
        proofCoolAvg:               (data as any).proofCoolAvg               ?? '',
        proofParticipationRows:     (data as any).proofParticipationRows     ?? '',
      });
      setProofSignalsForm({
        proofSignalsTitle:    (data as any).proofSignalsTitle    ?? 'Signal Quality',
        proofSignalsSubtitle: (data as any).proofSignalsSubtitle ?? 'Bullish/Bearish bias tracked over a fixed window',
        proofSignalWinRate:   (data as any).proofSignalWinRate   ?? '',
        proofSignalAvgMove:   (data as any).proofSignalAvgMove   ?? '',
        proofSignalMaxDD:     (data as any).proofSignalMaxDD     ?? '',
        proofSignalRows:      (data as any).proofSignalRows      ?? '',
      });
      setProofWinningTradesForm({
        proofWinningTradesTitle:    (data as any).proofWinningTradesTitle    ?? 'Winning Trades',
        proofWinningTradesSubtitle: (data as any).proofWinningTradesSubtitle ?? 'Most recent profitable closes from active strategy bots (same live exchange data as bot analytics)',
        showProofWinningTradesStrategyName: (data as any).showProofWinningTradesStrategyName ?? true,
      });
      // Load dynamic banners separately
      try {
        const bList = await landingAdminService.listBanners();
        setBanners(bList);
      } catch { /* banners table may not exist yet — silently ignore */ }
    } catch (err: any) {
      console.error('AdminLandingPageManager: load failed', err);
      showToast(`Failed to load landing page config (${err?.message ?? 'unknown error'})`, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, canManage, canView]);

  useEffect(() => { load(); }, [load]);

  // ── publish / unpublish ─────────────────────────────────────────────────────

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await landingAdminService.publish();
      showToast('🟢 Landing page is now LIVE');
      load();
    } catch {
      showToast('Failed to publish', 'error');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setPublishing(true);
    try {
      await landingAdminService.unpublish();
      showToast('⚪ Landing page hidden — visitors see the login page');
      load();
    } catch {
      showToast('Failed to unpublish', 'error');
    } finally {
      setPublishing(false);
    }
  };

  // ── section toggles — save immediately on click ─────────────────────────────

  const handleToggleSection = async (
    key:
      | 'showOracleFeed'
      | 'showBtcFilter'
      | 'showMicroReversalFilter'
      | 'showParticipation'
      | 'showSectorBreakdown'
      | 'showAiRegime'
      | 'showMajorCoinsMarket'
      | 'showMajorCoinsChartStyleOptions'
      | 'showSignalCardOracle'
      | 'showSignalCardBtcRegime'
      | 'showSignalCardMicroReversal'
      | 'showSignalCardParticipation'
      | 'showSignalCardRisk'
  ) => {
    if (!config || !canManage) return;
    const defaults: Record<typeof key, boolean> = {
      showOracleFeed: true,
      showBtcFilter: true,
      showMicroReversalFilter: false,
      showParticipation: true,
      showSectorBreakdown: false,
      showAiRegime: true,
      showMajorCoinsMarket: true,
      showMajorCoinsChartStyleOptions: true,
      showSignalCardOracle: true,
      showSignalCardBtcRegime: true,
      showSignalCardMicroReversal: true,
      showSignalCardParticipation: true,
      showSignalCardRisk: true,
    };
    const current = config[key] ?? defaults[key];
    const newVal = !current;
    // Optimistic update
    setConfig(prev => prev ? { ...prev, [key]: newVal } : prev);
    try {
      const updated = await landingAdminService.updateConfig({ [key]: newVal });
      setConfig(updated);
      const labelByKey: Record<typeof key, string> = {
        showOracleFeed: 'Oracle Feed',
        showBtcFilter: 'BTC Regime',
        showMicroReversalFilter: 'Micro-Reversal Filter',
        showParticipation: 'Participation',
        showSectorBreakdown: 'Sector breakdown tab',
        showAiRegime: 'AI Market Regime',
        showMajorCoinsMarket: 'Major Coins Market Cap',
        showMajorCoinsChartStyleOptions: 'Major Coins chart style selector',
        showSignalCardOracle: 'How Signals Work: Oracle card',
        showSignalCardBtcRegime: 'How Signals Work: BTC Regime card',
        showSignalCardMicroReversal: 'How Signals Work: Micro-Reversal card',
        showSignalCardParticipation: 'How Signals Work: Participation card',
        showSignalCardRisk: 'How Signals Work: Risk-Aware card',
      };
      showToast(`${labelByKey[key]} ${newVal ? 'shown' : 'hidden'}`);
    } catch {
      // Revert on error
      setConfig(prev => prev ? { ...prev, [key]: !newVal } : prev);
      showToast('Failed to save change', 'error');
    }
  };

  // ── oracle feed limit ───────────────────────────────────────────────────────

  const handleOracleLimitChange = async (limit: number) => {
    if (!canManage) return;
    setConfig(prev => prev ? { ...prev, oracleFeedLimit: limit } : prev);
    try {
      await landingAdminService.updateConfig({ oracleFeedLimit: limit });
    } catch {
      showToast('Failed to save limit', 'error');
    }
  };

  const handleMajorCoinsDefaultChartChange = async (chart: string) => {
    if (!canManage) return;
    const normalized = normalizeMajorCoinsChartType(chart);
    setConfig(prev => prev ? { ...prev, majorCoinsDefaultChart: normalized } : prev);
    try {
      const updated = await landingAdminService.updateConfig({ majorCoinsDefaultChart: normalized });
      setConfig(updated);
      showToast(`Default chart set to ${MAJOR_COINS_CHART_OPTIONS.find((o) => o.id === normalized)?.label ?? normalized}`);
    } catch {
      showToast('Failed to save default chart', 'error');
    }
  };

  // ── hero content save ───────────────────────────────────────────────────────

  const handleHeroSave = async () => {
    setSaving(true);
    try {
      const updated = await landingAdminService.updateConfig({
        heroTitle: heroForm.heroTitle,
        heroSubtitle: heroForm.heroSubtitle,
        heroCtaPrimary: heroForm.heroCtaPrimary,
        heroCtaPrimaryUrl: heroForm.heroCtaPrimaryUrl,
        heroCtaSecondaryUrl: heroForm.heroCtaSecondaryUrl,
        heroCtaSecondary: heroForm.heroCtaSecondary,
        announcement: heroForm.announcement,
      });
      setConfig(updated);
      setHeroEditing(false);
      showToast('Hero content saved');
    } catch {
      showToast('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const cancelHeroEdit = () => {
    if (!config) return;
    setHeroForm({
      heroTitle: config.heroTitle ?? '',
      heroSubtitle: config.heroSubtitle ?? '',
      heroCtaPrimary: config.heroCtaPrimary ?? '',
      heroCtaSecondary: config.heroCtaSecondary ?? '',
      heroCtaPrimaryUrl: config.heroCtaPrimaryUrl ?? '/register',
      heroCtaSecondaryUrl: config.heroCtaSecondaryUrl ?? '/login',
      announcement: config.announcement ?? '',
    });
    setHeroEditing(false);
  };

  const handleCtaBannerSave = async () => {
    setSaving(true);
    try {
      const updated = await landingAdminService.updateConfig({
        ctaBannerTitle:    ctaBannerForm.ctaBannerTitle,
        ctaBannerSubtitle: ctaBannerForm.ctaBannerSubtitle,
      });
      setConfig(updated);
      setCtaBannerEditing(false);
      showToast('Bottom CTA banner saved');
    } catch {
      showToast('Failed to save banner', 'error');
    } finally {
      setSaving(false);
    }
  };

  const cancelCtaBannerEdit = () => {
    if (!config) return;
    setCtaBannerForm({
      ctaBannerTitle:    (config as any).ctaBannerTitle    ?? 'Ready to Trade with an Edge?',
      ctaBannerSubtitle: (config as any).ctaBannerSubtitle ?? 'Join our trading platform to access automated strategies, personalised signal feeds, and real-time portfolio management.',
    });
    setCtaBannerEditing(false);
  };

  const handleSignalCardsSave = async () => {
    setSaving(true);
    try {
      const updated = await landingAdminService.updateConfig(signalCardsForm as any);
      setConfig(updated);
      setSignalCardsEditing(false);
      showToast('How Our Signals Work cards saved');
    } catch {
      showToast('Failed to save cards', 'error');
    } finally {
      setSaving(false);
    }
  };

  const cancelSignalCardsEdit = () => {
    if (!config) return;
    setSignalCardsForm({
      signalCardOracleIcon:         (config as any).signalCardOracleIcon         ?? '🔮',
      signalCardOracleTitle:        (config as any).signalCardOracleTitle        ?? 'Oracle Rankings',
      signalCardOracleDesc:         (config as any).signalCardOracleDesc         ?? 'AI-scored coin rankings updated in real-time, giving you the highest-conviction LONG and SHORT candidates.',
      signalCardBtcRegimeIcon:      (config as any).signalCardBtcRegimeIcon      ?? '📊',
      signalCardBtcRegimeTitle:     (config as any).signalCardBtcRegimeTitle     ?? 'BTC Regime Detection',
      signalCardBtcRegimeDesc:      (config as any).signalCardBtcRegimeDesc      ?? 'Understand whether the macro crypto market is bullish, bearish, or ranging before placing any trade.',
      signalCardParticipationIcon:  (config as any).signalCardParticipationIcon  ?? '🔥',
      signalCardParticipationTitle: (config as any).signalCardParticipationTitle ?? 'Participation Filter',
      signalCardParticipationDesc:  (config as any).signalCardParticipationDesc  ?? 'Know which coins are actually moving with volume behind them — not just random price noise.',
      signalCardRiskIcon:           (config as any).signalCardRiskIcon           ?? '🛡️',
      signalCardRiskTitle:          (config as any).signalCardRiskTitle          ?? 'Risk-Aware Signals',
      signalCardRiskDesc:           (config as any).signalCardRiskDesc           ?? 'RSI, EMA, funding rate and momentum all combined so you never trade against the current.',
    });
    setSignalCardsEditing(false);
  };

  const handleNavSave = async () => {
    setSaving(true);
    try {
      const updated = await landingAdminService.updateConfig({
        navBrandText: navForm.navBrandText,
        navTagline: navForm.navTagline,
        navBtn1Label: navForm.navBtn1Label,
        navBtn1Url: navForm.navBtn1Url,
        navBtn2Label: navForm.navBtn2Label,
        navBtn2Url: navForm.navBtn2Url,
        footerLink4Label: navForm.navBtn3Label,
        footerLink4Url: navForm.navBtn3Url,
      });
      setConfig(updated);
      setNavEditing(false);
      showToast('Nav bar saved');
    } catch {
      showToast('Failed to save nav bar', 'error');
    } finally {
      setSaving(false);
    }
  };

  const cancelNavEdit = () => {
    if (!config) return;
    setNavForm({
      navBrandText: config.navBrandText ?? 'Zogzila Client',
      navTagline: config.navTagline ?? 'MARKET SIGNALS',
      navBtn1Label: config.navBtn1Label ?? 'Login',
      navBtn1Url: config.navBtn1Url ?? '/login',
      navBtn2Label: config.navBtn2Label ?? 'Register',
      navBtn2Url: config.navBtn2Url ?? '/register/personal',
      navBtn3Label: config.footerLink4Label ?? 'Business Register',
      navBtn3Url: config.footerLink4Url ?? '/register/business',
    });
    setNavEditing(false);
  };

  // ── footer save / cancel ────────────────────────────────────────────────────

  const handleFooterSave = async () => {
    setSaving(true);
    try {
      const updated = await landingAdminService.updateConfig({
        footerCopyright:  footerForm.footerCopyright,
        footerLink1Label: footerForm.footerLink1Label,
        footerLink1Url:   footerForm.footerLink1Url,
        footerLink2Label: footerForm.footerLink2Label,
        footerLink2Url:   footerForm.footerLink2Url,
        footerLink3Label: footerForm.footerLink3Label,
        footerLink3Url:   footerForm.footerLink3Url,
        footerLink4Label: footerForm.footerLink4Label,
        footerLink4Url:   footerForm.footerLink4Url,
      });
      setConfig(updated);
      setFooterEditing(false);
      showToast('Footer saved');
    } catch {
      showToast('Failed to save footer', 'error');
    } finally {
      setSaving(false);
    }
  };

  const cancelFooterEdit = () => {
    if (!config) return;
    setFooterForm({
      footerCopyright:  config.footerCopyright ?? '',
      footerLink1Label: config.footerLink1Label ?? 'Login',
      footerLink1Url:   config.footerLink1Url   ?? '/login',
      footerLink2Label: config.footerLink2Label ?? 'Register',
      footerLink2Url:   config.footerLink2Url   ?? '/register',
      footerLink3Label: config.footerLink3Label ?? 'Personal',
      footerLink3Url:   config.footerLink3Url   ?? '/register/personal',
      footerLink4Label: config.footerLink4Label ?? 'Business',
      footerLink4Url:   config.footerLink4Url   ?? '/business/register',
    });
    setFooterEditing(false);
  };

  // ── proof section toggles — save immediately ────────────────────────────────

  const handleToggleMaskAllSections = async () => {
    if (!config || !canManage) return;
    const newVal = !(config.maskAllSections ?? false);
    setConfig(prev => (prev ? { ...prev, maskAllSections: newVal } : prev));
    try {
      const updated = await landingAdminService.updateConfig({ maskAllSections: newVal });
      setConfig(updated);
      showToast(
        newVal
          ? 'All main landing sections are hidden (nav stays visible)'
          : 'Sections visible again according to each toggle below'
      );
    } catch {
      setConfig(prev => (prev ? { ...prev, maskAllSections: !newVal } : prev));
      showToast('Failed to save change', 'error');
    }
  };

  const handleToggleShowPublicRefreshBanner = async () => {
    if (!config || !canManage) return;
    const newVal = !(config.showPublicRefreshBanner ?? true);
    setConfig(prev => (prev ? { ...prev, showPublicRefreshBanner: newVal } : prev));
    try {
      const updated = await landingAdminService.updateConfig({ showPublicRefreshBanner: newVal });
      setConfig(updated);
      showToast(newVal ? 'Public refresh strip will show on /market' : 'Public refresh strip hidden on /market');
    } catch {
      setConfig(prev => (prev ? { ...prev, showPublicRefreshBanner: !newVal } : prev));
      showToast('Failed to save change', 'error');
    }
  };

  // ── About section handlers ──────────────────────────────────────────────

  const handleToggleShowAbout = async () => {
    if (!config || !canManage) return;
    const newVal = !config.showAboutSection;
    setConfig(prev => prev ? { ...prev, showAboutSection: newVal } : prev);
    try {
      const updated = await landingAdminService.updateConfig({ showAboutSection: newVal } as any);
      setConfig(updated);
      showToast(`About section ${newVal ? 'shown' : 'hidden'} on landing page`);
    } catch {
      setConfig(prev => prev ? { ...prev, showAboutSection: !newVal } : prev);
      showToast('Failed to save change', 'error');
    }
  };

  const handleToggleMaskAbout = async () => {
    if (!config || !canManage) return;
    const newVal = !config.maskAboutSection;
    setConfig(prev => prev ? { ...prev, maskAboutSection: newVal } : prev);
    try {
      const updated = await landingAdminService.updateConfig({ maskAboutSection: newVal } as any);
      setConfig(updated);
      showToast(`About section ${newVal ? 'masked' : 'unmasked'}`);
    } catch {
      setConfig(prev => prev ? { ...prev, maskAboutSection: !newVal } : prev);
      showToast('Failed to save change', 'error');
    }
  };

  const handleAboutSave = async () => {
    setSaving(true);
    try {
      const updated = await landingAdminService.updateConfig(aboutForm as any);
      setConfig(updated);
      setAboutEditing(false);
      showToast('About section saved');
    } catch { showToast('Failed to save', 'error'); } finally { setSaving(false); }
  };

  const cancelAboutEdit = () => {
    if (!config) return;
    setAboutForm({
      aboutBadge:           (config as any).aboutBadge           ?? 'About Zogzila',
      aboutTitle:           (config as any).aboutTitle           ?? 'What is Zogzila?',
      aboutBody:            (config as any).aboutBody            ?? '',
      aboutHighlight1Icon:  (config as any).aboutHighlight1Icon  ?? '📡',
      aboutHighlight1Title: (config as any).aboutHighlight1Title ?? 'Real-Time Signals',
      aboutHighlight1Text:  (config as any).aboutHighlight1Text  ?? '',
      aboutHighlight2Icon:  (config as any).aboutHighlight2Icon  ?? '🧠',
      aboutHighlight2Title: (config as any).aboutHighlight2Title ?? 'AI-Powered Analysis',
      aboutHighlight2Text:  (config as any).aboutHighlight2Text  ?? '',
      aboutHighlight3Icon:  (config as any).aboutHighlight3Icon  ?? '🏆',
      aboutHighlight3Title: (config as any).aboutHighlight3Title ?? 'Proven Results',
      aboutHighlight3Text:  (config as any).aboutHighlight3Text  ?? '',
    });
    setAboutEditing(false);
  };

  // 'masked' | 2 | 5 | 0 (0 = all)
  const handleToggleWinningTradesFlag = async (
    key:
      | 'showProofLosingTrades'
      | 'showProofWinningTradesDailyPnl'
      | 'showProofWinningTradesDailyPnlPerStrategy'
  ) => {
    if (!config || !canManage) return;
    const newVal = !(config as any)[key];
    setConfig((prev) => (prev ? { ...prev, [key]: newVal } : prev));
    try {
      const updated = await landingAdminService.updateConfig({ [key]: newVal } as any);
      setConfig(updated);
      const label =
        key === 'showProofLosingTrades'
          ? 'Include losing trades'
          : key === 'showProofWinningTradesDailyPnlPerStrategy'
            ? 'Daily PNL (strategy) column'
            : 'Daily PNL (all bots) column';
      showToast(`${label} ${newVal ? 'enabled' : 'disabled'}`);
    } catch {
      setConfig((prev) => (prev ? { ...prev, [key]: !newVal } : prev));
      showToast('Failed to save change', 'error');
    }
  };

  const handleSetWinningTradesVisibility = async (mode: 'masked' | 0 | 2 | 5) => {
    if (!config || !canManage) return;
    const masked = mode === 'masked';
    const limit  = masked ? 0 : (mode as number);
    const prev = { maskWinningTrades: config.maskWinningTrades, winningTradesDisplayLimit: config.winningTradesDisplayLimit };
    setConfig(c => c ? { ...c, maskWinningTrades: masked, winningTradesDisplayLimit: limit } : c);
    try {
      const updated = await landingAdminService.updateConfig({ maskWinningTrades: masked, winningTradesDisplayLimit: limit } as any);
      setConfig(updated);
      const label = masked ? 'Masked (placeholder rows shown)' : limit === 0 ? 'All rows visible' : `Top ${limit} rows visible`;
      showToast(`Winning Trades: ${label}`);
    } catch {
      setConfig(c => c ? { ...c, ...prev } : c);
      showToast('Failed to save change', 'error');
    }
  };

  const handleToggleSectionMask = async (key: 'maskBtcRegime' | 'maskMicroReversal' | 'maskParticipation' | 'maskAiRegime' | 'maskMajorCoinsMarket') => {
    if (!config || !canManage) return;
    const current = config[key] ?? false;
    const newVal = !current;
    const prev = current;
    setConfig(c => c ? { ...c, [key]: newVal } : c);
    try {
      const updated = await landingAdminService.updateConfig({ [key]: newVal } as any);
      // Merge the response — if the backend doesn't return the field, keep our local value
      setConfig(c => c ? { ...c, ...updated, [key]: (updated as any)[key] ?? newVal } : c);
      const label = key === 'maskBtcRegime'
        ? 'BTC Market Regime'
        : key === 'maskMicroReversal'
          ? 'Micro-Reversal Filter'
          : key === 'maskAiRegime'
            ? 'AI Market Regime'
            : key === 'maskMajorCoinsMarket'
              ? 'Major Coins Market Cap'
            : 'Meme Participation';
      showToast(`${label}: ${newVal ? 'masked (placeholder shown)' : 'unmasked (live data visible)'}`);
    } catch {
      setConfig(c => c ? { ...c, [key]: prev } : c);
      showToast('Failed to save change', 'error');
    }
  };

  // ── Dynamic banner handlers ───────────────────────────────────────────────

  const handleAddBanner = async (side: 'LEFT' | 'RIGHT') => {
    if (!canManage) return;
    setBannerSaving('new');
    try {
      const newB = await landingAdminService.createBanner({
        side, sortOrder: banners.filter(b => b.side === side).length, visible: false, masked: false,
      });
      setBanners(prev => [...prev, newB]);
      showToast(`Banner added on ${side} side`);
    } catch { showToast('Failed to add banner', 'error'); }
    finally { setBannerSaving(null); }
  };

  const handleUpdateBanner = async (id: number, field: keyof BannerRow, value: any) => {
    if (!canManage) return;
    setBanners(prev => prev.map(b => b.id === id ? { ...b, [field]: value } : b));
    setBannerSaving(id);
    try {
      const updated = await landingAdminService.updateBanner(id, { [field]: value });
      setBanners(prev => prev.map(b => b.id === id ? updated : b));
    } catch { showToast('Failed to save banner change', 'error'); }
    finally { setBannerSaving(null); }
  };

  const handleDeleteBanner = async (id: number) => {
    if (!canManage || !window.confirm('Delete this banner?')) return;
    setBannerSaving(id);
    try {
      await landingAdminService.deleteBanner(id);
      setBanners(prev => prev.filter(b => b.id !== id));
      showToast('Banner deleted');
    } catch { showToast('Failed to delete banner', 'error'); }
    finally { setBannerSaving(null); }
  };

  const handleToggleProof = async (key: 'showProofRegime' | 'showProofParticipation' | 'showProofSignals' | 'showProofWinningTrades') => {
    if (!config || !canManage) return;
    const newVal = !config[key];
    setConfig(prev => prev ? { ...prev, [key]: newVal } : prev);
    try {
      const updated = await landingAdminService.updateConfig({ [key]: newVal } as any);
      setConfig(updated);
      const label =
        key === 'showProofRegime' ? 'Market Accuracy'
          : key === 'showProofParticipation' ? 'Participation Proof'
            : key === 'showProofSignals' ? 'Signal Quality'
              : 'Winning Trades';
      showToast(`${label} section ${newVal ? 'shown' : 'hidden'}`);
    } catch {
      setConfig(prev => prev ? { ...prev, [key]: !newVal } : prev);
      showToast('Failed to save change', 'error');
    }
  };

  // ── proof regime save ───────────────────────────────────────────────────────

  const handleProofRegimeSave = async () => {
    setSaving(true);
    try {
      const updated = await landingAdminService.updateConfig(proofRegimeForm as any);
      setConfig(updated);
      setProofRegimeEditing(false);
      showToast('Market Accuracy section saved');
    } catch { showToast('Failed to save', 'error'); } finally { setSaving(false); }
  };

  const cancelProofRegimeEdit = () => {
    if (!config) return;
    setProofRegimeForm({
      proofRegimeTitle:    (config as any).proofRegimeTitle    ?? 'Market Condition Accuracy',
      proofRegimeSubtitle: (config as any).proofRegimeSubtitle ?? '',
      proofRegimeAccuracy: (config as any).proofRegimeAccuracy ?? '',
      proofRegimeRows:     (config as any).proofRegimeRows     ?? '',
    });
    setProofRegimeEditing(false);
  };

  // ── proof participation save ────────────────────────────────────────────────

  const handleProofParticipationSave = async () => {
    setSaving(true);
    try {
      const updated = await landingAdminService.updateConfig(proofParticipationForm as any);
      setConfig(updated);
      setProofParticipationEditing(false);
      showToast('Participation Proof section saved');
    } catch { showToast('Failed to save', 'error'); } finally { setSaving(false); }
  };

  const cancelProofParticipationEdit = () => {
    if (!config) return;
    setProofParticipationForm({
      proofParticipationTitle:    (config as any).proofParticipationTitle    ?? 'Participation Filter Effectiveness',
      proofParticipationSubtitle: (config as any).proofParticipationSubtitle ?? '',
      proofHotAvg:                (config as any).proofHotAvg                ?? '',
      proofCoolAvg:               (config as any).proofCoolAvg               ?? '',
      proofParticipationRows:     (config as any).proofParticipationRows     ?? '',
    });
    setProofParticipationEditing(false);
  };

  // ── proof signals save ──────────────────────────────────────────────────────

  const handleProofSignalsSave = async () => {
    setSaving(true);
    try {
      const updated = await landingAdminService.updateConfig(proofSignalsForm as any);
      setConfig(updated);
      setProofSignalsEditing(false);
      showToast('Signal Quality section saved');
    } catch { showToast('Failed to save', 'error'); } finally { setSaving(false); }
  };

  const cancelProofSignalsEdit = () => {
    if (!config) return;
    setProofSignalsForm({
      proofSignalsTitle:    (config as any).proofSignalsTitle    ?? 'Signal Quality',
      proofSignalsSubtitle: (config as any).proofSignalsSubtitle ?? '',
      proofSignalWinRate:   (config as any).proofSignalWinRate   ?? '',
      proofSignalAvgMove:   (config as any).proofSignalAvgMove   ?? '',
      proofSignalMaxDD:     (config as any).proofSignalMaxDD     ?? '',
      proofSignalRows:      (config as any).proofSignalRows      ?? '',
    });
    setProofSignalsEditing(false);
  };

  // ── proof winning trades save ─────────────────────────────────────────────

  const handleProofWinningTradesSave = async () => {
    setSaving(true);
    try {
      const updated = await landingAdminService.updateConfig(proofWinningTradesForm as any);
      setConfig(updated);
      setProofWinningTradesEditing(false);
      showToast('Winning Trades section saved');
    } catch { showToast('Failed to save', 'error'); } finally { setSaving(false); }
  };

  const cancelProofWinningTradesEdit = () => {
    if (!config) return;
    setProofWinningTradesForm({
      proofWinningTradesTitle:    (config as any).proofWinningTradesTitle    ?? 'Winning Trades',
      proofWinningTradesSubtitle: (config as any).proofWinningTradesSubtitle ?? '',
      showProofWinningTradesStrategyName: (config as any).showProofWinningTradesStrategyName ?? true,
    });
    setProofWinningTradesEditing(false);
  };

  // ── render guards ──────────────────────────────────────────────────────────

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>Loading…</div>;
  }

  if (!canView) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
        You do not have permission to view the landing page management panel.
      </div>
    );
  }

  const published = config?.published ?? false;

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '4px 8px 40px' }}>

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: toast.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1.5px solid ${toast.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          color: toast.type === 'success' ? '#15803d' : '#b91c1c',
          borderRadius: 10, padding: '12px 20px', fontSize: 14, fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)', maxWidth: 380,
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', margin: '0 0 6px' }}>
          Market Intelligence Landing Page
        </h2>
        <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
          {canManage
            ? 'Full management access — edit content, toggle sections, publish or unpublish.'
            : 'Read-only view. Ask a SUPER_ADMIN to grant MANAGE_LANDING_PAGE to make changes.'}
        </p>
      </div>

      {/* ── 1. Publish status ─────────────────────────────────────────────── */}
      <div style={{
        background: published ? '#f0fdf4' : '#f8fafc',
        border: `2px solid ${published ? '#86efac' : '#e2e8f0'}`,
        borderRadius: 14, padding: '18px 24px', marginBottom: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 14,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
            background: published ? '#22c55e' : '#94a3b8',
            boxShadow: published ? '0 0 0 4px #dcfce7' : 'none',
          }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
              {published ? 'LIVE — Public page is visible to all visitors' : 'HIDDEN — Visitors are redirected to login'}
            </div>
            {config?.updatedBy && (
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>
                Last updated by <strong>{config.updatedBy}</strong>
                {config.updatedAt ? ` · ${new Date(config.updatedAt).toLocaleString()}` : ''}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => navigate('/admin/dashboard?tab=marketSignals')}
            style={{ fontSize: 13, color: '#2563eb', textDecoration: 'none', fontWeight: 600, padding: '8px 14px', border: '1.5px solid #bfdbfe', borderRadius: 8, background: '#eff6ff', cursor: 'pointer' }}
          >
            Preview
          </button>
          {canManage && (
            published ? (
              <button onClick={handleUnpublish} disabled={publishing}
                style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: publishing ? 'not-allowed' : 'pointer', opacity: publishing ? 0.6 : 1 }}>
                {publishing ? 'Unpublishing…' : 'Unpublish'}
              </button>
            ) : (
              <button onClick={handlePublish} disabled={publishing}
                style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontSize: 13, fontWeight: 700, cursor: publishing ? 'not-allowed' : 'pointer', opacity: publishing ? 0.6 : 1 }}>
                {publishing ? 'Publishing…' : '🚀 Publish Page'}
              </button>
            )
          )}
        </div>
      </div>

      {/* ── 1b. Mask all main content (single master switch) ───────────────── */}
      <Card style={{ border: '2px solid #fde68a', background: '#fffbeb' }}>
        <SectionTitle
          badge={
            <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
              Master
            </span>
          }
        >
          Mask all landing sections
        </SectionTitle>
        <p style={{ fontSize: 12, color: '#92400e', margin: '0 0 14px', lineHeight: 1.6 }}>
          When enabled, visitors only see the top bar (logo + login/register). Hero, live signals, “How our signals work”, proof blocks, bottom CTA, and footer are hidden. Your per-section toggles are not changed — turn this off to restore them.
        </p>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '12px 16px', borderRadius: 10,
          background: (config?.maskAllSections ?? false) ? '#fef2f2' : '#f0fdf4',
          border: `1.5px solid ${(config?.maskAllSections ?? false) ? '#fecaca' : '#bbf7d0'}`,
        }}>
          <span style={{ fontSize: 22 }}>{(config?.maskAllSections ?? false) ? '🙈' : '👁️'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
              {(config?.maskAllSections ?? false) ? 'Main content is masked' : 'Main content is visible'}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
              Public API skips loading market snapshot while masked (faster page).
            </div>
          </div>
          <Toggle
            checked={!!(config?.maskAllSections ?? false)}
            onChange={handleToggleMaskAllSections}
            disabled={!canManage}
          />
        </div>
      </Card>

      {/* ── 1c. Public “refresh / public view” strip on /market ───────────────── */}
      <Card>
        <SectionTitle
          badge={
            <span style={{ fontSize: 11, background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
              /market
            </span>
          }
        >
          Public refresh notice bar
        </SectionTitle>
        <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 14px', lineHeight: 1.6 }}>
          The light-blue bar above the hero on the public market page (“Public view shows layout only…” and the <strong>Refresh</strong> button). Turn off to hide it entirely (layout-only message and manual refresh).
        </p>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '12px 16px', borderRadius: 10,
          background: (config?.showPublicRefreshBanner ?? true) ? '#f0fdf4' : '#f8fafc',
          border: `1.5px solid ${(config?.showPublicRefreshBanner ?? true) ? '#bbf7d0' : '#e2e8f0'}`,
        }}>
          <span style={{ fontSize: 22 }}>{(config?.showPublicRefreshBanner ?? true) ? '👁️' : '🚫'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
              {(config?.showPublicRefreshBanner ?? true) ? 'Strip is visible' : 'Strip is hidden'}
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
              Applies to public <strong>/market</strong> (and landing when the signal area is shown). Signed-in viewers with live data still see real values; this only toggles the bar UI.
            </div>
          </div>
          <Toggle
            checked={config?.showPublicRefreshBanner ?? true}
            onChange={handleToggleShowPublicRefreshBanner}
            disabled={!canManage}
          />
        </div>
      </Card>

      {/* ── 2. Live signal cards (each card = one toggle, same pattern as Winning Trades) ─ */}
      <Card>
        <SectionTitle
          badge={
            <span style={{ fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
              Market Signals
            </span>
          }
        >
          Live data on landing / market page
        </SectionTitle>
        <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px', lineHeight: 1.6 }}>
          Turn each card on or off independently (saved immediately). This matches the public <strong>/</strong> and <strong>/market</strong> signal area — separate from “Proof” sections below.
        </p>

        {/* BTC Market Regime */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>📊</span> BTC Market Regime
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10,
            background: (config?.showBtcFilter ?? true) ? '#f0fdf4' : '#f8fafc',
            border: `1.5px solid ${(config?.showBtcFilter ?? true) ? '#bbf7d0' : '#e2e8f0'}`,
            marginBottom: 6,
          }}>
            <span style={{ fontSize: 20 }}>{(config?.showBtcFilter ?? true) ? '👁️' : '🚫'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show this card on the landing page</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                BULLISH / RANGING / BEARISH badge, BTC price and 24h change
              </div>
            </div>
            <Toggle
              checked={config?.showBtcFilter ?? true}
              onChange={() => handleToggleSection('showBtcFilter')}
              disabled={!canManage}
            />
          </div>
          {/* Individual mask row — visible even when global maskAllSections is on */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '10px 16px', borderRadius: 10,
            background: (config?.maskBtcRegime ?? false) ? '#fef2f2' : '#f0fdf4',
            border: `1.5px solid ${(config?.maskBtcRegime ?? false) ? '#fecaca' : '#bbf7d0'}`,
          }}>
            <span style={{ fontSize: 18 }}>{(config?.maskBtcRegime ?? false) ? '🔒' : '✅'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                {(config?.maskBtcRegime ?? false) ? 'Masked — showing blurred placeholder' : 'Live data visible on landing page'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                Toggle ON = live data shown. Toggle OFF = blurred placeholder. Overrides the global mask.
              </div>
            </div>
            <Toggle
              checked={!(config?.maskBtcRegime ?? false)}
              onChange={() => handleToggleSectionMask('maskBtcRegime')}
              disabled={!canManage}
            />
          </div>
        </div>

        {/* Micro-Reversal Filter */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>⚡</span> Micro-Reversal Filter
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10,
            background: (config?.showMicroReversalFilter ?? false) ? '#f0fdf4' : '#f8fafc',
            border: `1.5px solid ${(config?.showMicroReversalFilter ?? false) ? '#bbf7d0' : '#e2e8f0'}`,
            marginBottom: 6,
          }}>
            <span style={{ fontSize: 20 }}>{(config?.showMicroReversalFilter ?? false) ? '👁️' : '🚫'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show this card on the landing page</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                BTC 5m chop trap detector — blocks oracle signals when active
              </div>
            </div>
            <Toggle
              checked={config?.showMicroReversalFilter ?? false}
              onChange={() => handleToggleSection('showMicroReversalFilter')}
              disabled={!canManage}
            />
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '10px 16px', borderRadius: 10,
            background: (config?.maskMicroReversal ?? false) ? '#fef2f2' : '#f0fdf4',
            border: `1.5px solid ${(config?.maskMicroReversal ?? false) ? '#fecaca' : '#bbf7d0'}`,
          }}>
            <span style={{ fontSize: 18 }}>{(config?.maskMicroReversal ?? false) ? '🔒' : '✅'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                {(config?.maskMicroReversal ?? false) ? 'Masked — showing blurred placeholder' : 'Live data visible on landing page'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                Toggle ON = live data shown. Toggle OFF = blurred placeholder. Overrides the global mask.
              </div>
            </div>
            <Toggle
              checked={!(config?.maskMicroReversal ?? false)}
              onChange={() => handleToggleSectionMask('maskMicroReversal')}
              disabled={!canManage}
            />
          </div>
        </div>

        {/* Meme Participation */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>🔥</span> Meme Participation
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10,
            background: (config?.showParticipation ?? true) ? '#f0fdf4' : '#f8fafc',
            border: `1.5px solid ${(config?.showParticipation ?? true) ? '#bbf7d0' : '#e2e8f0'}`,
            marginBottom: 6,
          }}>
            <span style={{ fontSize: 20 }}>{(config?.showParticipation ?? true) ? '👁️' : '🚫'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show this card on the landing page</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                Participation level, active coins, and coin activity breakdown
              </div>
            </div>
            <Toggle
              checked={config?.showParticipation ?? true}
              onChange={() => handleToggleSection('showParticipation')}
              disabled={!canManage}
            />
          </div>
          {/* Individual mask row */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '10px 16px', borderRadius: 10,
            background: (config?.maskParticipation ?? false) ? '#fef2f2' : '#f0fdf4',
            border: `1.5px solid ${(config?.maskParticipation ?? false) ? '#fecaca' : '#bbf7d0'}`,
          }}>
            <span style={{ fontSize: 18 }}>{(config?.maskParticipation ?? false) ? '🔒' : '✅'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                {(config?.maskParticipation ?? false) ? 'Masked — showing blurred placeholder' : 'Live data visible on landing page'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                Toggle ON = live data shown. Toggle OFF = blurred placeholder. Overrides the global mask.
              </div>
            </div>
            <Toggle
              checked={!(config?.maskParticipation ?? false)}
              onChange={() => handleToggleSectionMask('maskParticipation')}
              disabled={!canManage}
            />
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '10px 16px', borderRadius: 10,
            background: (config?.showSectorBreakdown ?? false) ? '#eff6ff' : '#f8fafc',
            border: `1.5px solid ${(config?.showSectorBreakdown ?? false) ? '#bfdbfe' : '#e2e8f0'}`,
            marginTop: 6,
          }}>
            <span style={{ fontSize: 18 }}>{(config?.showSectorBreakdown ?? false) ? '📊' : '➖'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Sector breakdown tab (Coin Activity)</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                Adds Flat | By sector toggle — Layer 1, DeFi, Gaming, NFT, Meme. Does not change meme participation or bots.
              </div>
            </div>
            <Toggle
              checked={config?.showSectorBreakdown ?? false}
              onChange={() => handleToggleSection('showSectorBreakdown')}
              disabled={!canManage || !(config?.showParticipation ?? true)}
            />
          </div>
        </div>

        {/* AI Market Regime */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>🤖</span> AI Market Regime
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10,
            background: (config?.showAiRegime ?? true) ? '#f0fdf4' : '#f8fafc',
            border: `1.5px solid ${(config?.showAiRegime ?? true) ? '#bbf7d0' : '#e2e8f0'}`,
            marginBottom: 6,
          }}>
            <span style={{ fontSize: 20 }}>{(config?.showAiRegime ?? true) ? '👁️' : '🚫'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show this card on the landing page</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                Combined regime badge — HOT_MEME · HIGH_VOLATILITY · LOW_PARTICIPATION · BEARISH_BTC · MODERATE
              </div>
            </div>
            <Toggle
              checked={config?.showAiRegime ?? true}
              onChange={() => handleToggleSection('showAiRegime')}
              disabled={!canManage}
            />
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '10px 16px', borderRadius: 10,
            background: (config?.maskAiRegime ?? false) ? '#fef2f2' : '#f0fdf4',
            border: `1.5px solid ${(config?.maskAiRegime ?? false) ? '#fecaca' : '#bbf7d0'}`,
          }}>
            <span style={{ fontSize: 18 }}>{(config?.maskAiRegime ?? false) ? '🔒' : '✅'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                {(config?.maskAiRegime ?? false) ? 'Masked — showing blurred placeholder' : 'Live data visible on landing page'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                Toggle ON = live regime shown. Toggle OFF = blurred placeholder. Overrides the global mask.
              </div>
            </div>
            <Toggle
              checked={!(config?.maskAiRegime ?? false)}
              onChange={() => handleToggleSectionMask('maskAiRegime')}
              disabled={!canManage}
            />
          </div>
        </div>

        {/* Major Coins Market Cap */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>📈</span> Major Coins Market Cap
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10,
            background: (config?.showMajorCoinsMarket ?? true) ? '#f0fdf4' : '#f8fafc',
            border: `1.5px solid ${(config?.showMajorCoinsMarket ?? true) ? '#bbf7d0' : '#e2e8f0'}`,
            marginBottom: 6,
          }}>
            <span style={{ fontSize: 20 }}>{(config?.showMajorCoinsMarket ?? true) ? '👁️' : '🚫'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show this card on the landing page</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                Top 10 majors — market cap share chart + price / 24h table (BTC, ETH, SOL, …)
              </div>
            </div>
            <Toggle
              checked={config?.showMajorCoinsMarket ?? true}
              onChange={() => handleToggleSection('showMajorCoinsMarket')}
              disabled={!canManage}
            />
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '10px 16px', borderRadius: 10,
            background: (config?.maskMajorCoinsMarket ?? false) ? '#fef2f2' : '#f0fdf4',
            border: `1.5px solid ${(config?.maskMajorCoinsMarket ?? false) ? '#fecaca' : '#bbf7d0'}`,
          }}>
            <span style={{ fontSize: 18 }}>{(config?.maskMajorCoinsMarket ?? false) ? '🔒' : '✅'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                {(config?.maskMajorCoinsMarket ?? false) ? 'Masked — showing blurred placeholder' : 'Live data visible on landing page'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                Toggle ON = live data shown. Toggle OFF = blurred placeholder. Overrides the global mask.
              </div>
            </div>
            <Toggle
              checked={!(config?.maskMajorCoinsMarket ?? false)}
              onChange={() => handleToggleSectionMask('maskMajorCoinsMarket')}
              disabled={!canManage}
            />
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10,
            background: (config?.showMajorCoinsChartStyleOptions ?? true) ? '#f0fdf4' : '#f8fafc',
            border: `1.5px solid ${(config?.showMajorCoinsChartStyleOptions ?? true) ? '#bbf7d0' : '#e2e8f0'}`,
            marginTop: 6,
            marginBottom: 6,
          }}>
            <span style={{ fontSize: 20 }}>{(config?.showMajorCoinsChartStyleOptions ?? true) ? '👁️' : '🚫'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show chart style selector on public card</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                When hidden, visitors only see the admin default chart (no Donut / Bars / Treemap pills)
              </div>
            </div>
            <Toggle
              checked={config?.showMajorCoinsChartStyleOptions ?? true}
              onChange={() => handleToggleSection('showMajorCoinsChartStyleOptions')}
              disabled={!canManage}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: 20 }}>📊</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Default chart style</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                Used when the selector is hidden, or as the initial chart when it is shown
              </div>
            </div>
            <select
              value={normalizeMajorCoinsChartType(config?.majorCoinsDefaultChart)}
              onChange={(e) => handleMajorCoinsDefaultChartChange(e.target.value)}
              disabled={!canManage}
              style={{
                minWidth: 140,
                padding: '7px 10px',
                border: '1.5px solid #93c5fd',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                color: '#1e293b',
                background: canManage ? '#fff' : '#f8fafc',
              }}
            >
              {MAJOR_COINS_CHART_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label} — {opt.hint}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Oracle Signal Feed */}
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>🔮</span> Oracle Signal Feed
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, marginBottom: 12,
            background: (config?.showOracleFeed ?? true) ? '#f0fdf4' : '#f8fafc',
            border: `1.5px solid ${(config?.showOracleFeed ?? true) ? '#bbf7d0' : '#e2e8f0'}`,
          }}>
            <span style={{ fontSize: 20 }}>{(config?.showOracleFeed ?? true) ? '👁️' : '🚫'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show this card on the landing page</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                Ranked coins with LONG / SHORT / WATCH and confidence
              </div>
            </div>
            <Toggle
              checked={config?.showOracleFeed ?? true}
              onChange={() => handleToggleSection('showOracleFeed')}
              disabled={!canManage}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: 20 }}>🔢</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Coins shown in Oracle feed</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>Maximum rows in the signal table (when the feed is visible)</div>
            </div>
            <input
              type="number" min={3} max={50}
              value={config?.oracleFeedLimit ?? 10}
              onChange={e => handleOracleLimitChange(parseInt(e.target.value) || 10)}
              disabled={!canManage}
              style={{
                width: 64, padding: '7px 10px', border: '1.5px solid #93c5fd',
                borderRadius: 8, fontSize: 14, fontWeight: 700, textAlign: 'center',
                color: '#1e293b', background: canManage ? '#fff' : '#f8fafc',
              }}
            />
          </div>
        </div>
      </Card>

      {/* ── 3. How Our Signals Work cards ─────────────────────────────────── */}
      <Card>
        <SectionTitle
          badge={canManage
            ? <span style={{ fontSize: 11, background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Editable</span>
            : undefined
          }
        >
          How Our Signals Work cards
        </SectionTitle>
        <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 14px', lineHeight: 1.6 }}>
          Update icon/title/description text and hide individual cards. Visibility toggles save immediately; text and icon edits require Save.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginBottom: 16 }}>
          {[
            { key: 'showSignalCardOracle' as const, icon: '🔮', title: 'Oracle Rankings' },
            { key: 'showSignalCardBtcRegime' as const, icon: '📊', title: 'BTC Regime Detection' },
            { key: 'showSignalCardParticipation' as const, icon: '🔥', title: 'Participation Filter' },
            { key: 'showSignalCardRisk' as const, icon: '🛡️', title: 'Risk-Aware Signals' },
          ].map(card => {
            const visible = config?.[card.key] ?? true;
            return (
              <div key={card.key} style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '10px 16px', borderRadius: 10,
                background: visible ? '#f0fdf4' : '#f8fafc',
                border: `1.5px solid ${visible ? '#bbf7d0' : '#e2e8f0'}`,
              }}>
                <span style={{ fontSize: 20 }}>{visible ? '👁️' : '🚫'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{card.icon} {card.title}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                    {visible ? 'Visible on landing page' : 'Hidden from landing page'}
                  </div>
                </div>
                <Toggle
                  checked={visible}
                  onChange={() => handleToggleSection(card.key)}
                  disabled={!canManage}
                />
              </div>
            );
          })}
        </div>

        {!signalCardsEditing ? (
          <button
            onClick={() => setSignalCardsEditing(true)}
            disabled={!canManage}
            style={{
              background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 16px', fontSize: 13, fontWeight: 700,
              cursor: !canManage ? 'not-allowed' : 'pointer', opacity: !canManage ? 0.5 : 1,
            }}
          >
            Edit card text and icons
          </button>
        ) : (
          <>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 10 }}>Oracle Rankings card</div>
              <FieldInput label="Icon" value={signalCardsForm.signalCardOracleIcon} onChange={v => setSignalCardsForm(f => ({ ...f, signalCardOracleIcon: v }))} disabled={!canManage} placeholder="🔮" />
              <FieldInput label="Title" value={signalCardsForm.signalCardOracleTitle} onChange={v => setSignalCardsForm(f => ({ ...f, signalCardOracleTitle: v }))} disabled={!canManage} />
              <FieldInput label="Description" value={signalCardsForm.signalCardOracleDesc} onChange={v => setSignalCardsForm(f => ({ ...f, signalCardOracleDesc: v }))} disabled={!canManage} multiline />
            </div>

            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 10 }}>BTC Regime Detection card</div>
              <FieldInput label="Icon" value={signalCardsForm.signalCardBtcRegimeIcon} onChange={v => setSignalCardsForm(f => ({ ...f, signalCardBtcRegimeIcon: v }))} disabled={!canManage} placeholder="📊" />
              <FieldInput label="Title" value={signalCardsForm.signalCardBtcRegimeTitle} onChange={v => setSignalCardsForm(f => ({ ...f, signalCardBtcRegimeTitle: v }))} disabled={!canManage} />
              <FieldInput label="Description" value={signalCardsForm.signalCardBtcRegimeDesc} onChange={v => setSignalCardsForm(f => ({ ...f, signalCardBtcRegimeDesc: v }))} disabled={!canManage} multiline />
            </div>

            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 10 }}>Participation Filter card</div>
              <FieldInput label="Icon" value={signalCardsForm.signalCardParticipationIcon} onChange={v => setSignalCardsForm(f => ({ ...f, signalCardParticipationIcon: v }))} disabled={!canManage} placeholder="🔥" />
              <FieldInput label="Title" value={signalCardsForm.signalCardParticipationTitle} onChange={v => setSignalCardsForm(f => ({ ...f, signalCardParticipationTitle: v }))} disabled={!canManage} />
              <FieldInput label="Description" value={signalCardsForm.signalCardParticipationDesc} onChange={v => setSignalCardsForm(f => ({ ...f, signalCardParticipationDesc: v }))} disabled={!canManage} multiline />
            </div>

            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 10 }}>Risk-Aware Signals card</div>
              <FieldInput label="Icon" value={signalCardsForm.signalCardRiskIcon} onChange={v => setSignalCardsForm(f => ({ ...f, signalCardRiskIcon: v }))} disabled={!canManage} placeholder="🛡️" />
              <FieldInput label="Title" value={signalCardsForm.signalCardRiskTitle} onChange={v => setSignalCardsForm(f => ({ ...f, signalCardRiskTitle: v }))} disabled={!canManage} />
              <FieldInput label="Description" value={signalCardsForm.signalCardRiskDesc} onChange={v => setSignalCardsForm(f => ({ ...f, signalCardRiskDesc: v }))} disabled={!canManage} multiline />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleSignalCardsSave}
                disabled={saving || !canManage}
                style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: saving || !canManage ? 'not-allowed' : 'pointer', opacity: saving || !canManage ? 0.6 : 1 }}
              >
                {saving ? 'Saving…' : 'Save cards'}
              </button>
              <button
                onClick={cancelSignalCardsEdit}
                disabled={saving}
                style={{ background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </Card>

      {/* ── 4. Hero CTA button visibility ─────────────────────────────────── */}
      <Card>
        <SectionTitle
          badge={canManage
            ? <span style={{ fontSize: 11, background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Click to toggle</span>
            : <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Read-only</span>}
        >
          Hero Buttons
        </SectionTitle>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {([
            { key: 'showCtaPrimary'   as const, label: config?.heroCtaPrimary   || 'Primary Button',   desc: `Links to: ${config?.heroCtaPrimaryUrl   || '/register'}` },
            { key: 'showCtaSecondary' as const, label: config?.heroCtaSecondary || 'Secondary Button', desc: `Links to: ${config?.heroCtaSecondaryUrl || '/login'}` },
          ]).map(({ key, label, desc }) => {
            const visible = config?.[key] ?? true;
            return (
              <div key={key} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '12px 16px', borderRadius: 10,
                background: visible ? '#f0fdf4' : '#f8fafc',
                border: `1.5px solid ${visible ? '#bbf7d0' : '#e2e8f0'}`,
              }}>
                <span style={{ fontSize: 20 }}>{visible ? '👁️' : '🚫'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{label}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{desc}</div>
                </div>
                <Toggle
                  checked={visible}
                  onChange={async () => {
                    if (!canManage || !config) return;
                    const newVal = !config[key];
                    setConfig(prev => prev ? { ...prev, [key]: newVal } : prev);
                    try {
                      const updated = await landingAdminService.updateConfig({ [key]: newVal });
                      setConfig(updated);
                      showToast(`${label} ${newVal ? 'shown' : 'hidden'} on landing page`);
                    } catch {
                      setConfig(prev => prev ? { ...prev, [key]: !newVal } : prev);
                      showToast('Failed to save change', 'error');
                    }
                  }}
                  disabled={!canManage}
                />
              </div>
            );
          })}
        </div>
      </Card>

      {/* ── 4. Nav Bar ─────────────────────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <SectionTitle>Nav Bar</SectionTitle>
          {canManage && !navEditing && (
            <button onClick={() => setNavEditing(true)}
              style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              ✏️ Edit
            </button>
          )}
        </div>

        {/* Live preview strip */}
        {!navEditing && (
          <div style={{ background: '#1e293b', borderRadius: 10, padding: '12px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 26, height: 26, borderRadius: 6, background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#fff', fontWeight: 800, fontSize: 13 }}>Z</span>
              </div>
              <span style={{ fontWeight: 800, fontSize: 14, color: '#fff' }}>{config?.navBrandText || 'Zogzila Client'}</span>
              <span style={{ fontSize: 10, background: '#1e40af', color: '#93c5fd', padding: '2px 7px', borderRadius: 20, fontWeight: 600 }}>
                {config?.navTagline || 'MARKET SIGNALS'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {config?.navBtn1Visible !== false && (
                <div style={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '5px 12px', fontSize: 12, color: '#fff' }}>
                  {config?.navBtn1Label || 'Login'}
                </div>
              )}
              {config?.navBtn2Visible !== false && (
                <div style={{ background: '#2563eb', borderRadius: 6, padding: '5px 12px', fontSize: 12, color: '#fff', fontWeight: 600 }}>
                  {config?.navBtn2Label || 'Register'}
                </div>
              )}
              {config?.footerLink4Visible !== false && (
                <div style={{ background: '#0d9488', borderRadius: 6, padding: '5px 12px', fontSize: 12, color: '#fff', fontWeight: 600 }}>
                  {config?.footerLink4Label || 'Business Register'}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Button visibility toggles — always visible, no edit mode needed */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: navEditing ? 16 : 0 }}>
          {([
            { key: 'navBtn1Visible' as const, label: config?.navBtn1Label || 'Button 1', desc: config?.navBtn1Url || '/login' },
            { key: 'navBtn2Visible' as const, label: config?.navBtn2Label || 'Button 2', desc: config?.navBtn2Url || '/register/personal' },
            { key: 'footerLink4Visible' as const, label: config?.footerLink4Label || 'Business Register', desc: config?.footerLink4Url || '/register/business' },
          ]).map(({ key, label, desc }) => {
            const visible = config?.[key] ?? true;
            return (
              <div key={key} style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '10px 14px', borderRadius: 10,
                background: visible ? '#f0fdf4' : '#f8fafc', border: `1.5px solid ${visible ? '#bbf7d0' : '#e2e8f0'}`,
              }}>
                <span style={{ fontSize: 18 }}>{visible ? '👁️' : '🚫'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{label}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>→ {desc}</div>
                </div>
                <Toggle
                  checked={visible}
                  onChange={async () => {
                    if (!canManage || !config) return;
                    const newVal = !config[key];
                    setConfig(prev => prev ? { ...prev, [key]: newVal } : prev);
                    try {
                      const updated = await landingAdminService.updateConfig({ [key]: newVal });
                      setConfig(updated);
                      showToast(`"${label}" ${newVal ? 'shown' : 'hidden'} in nav bar`);
                    } catch {
                      setConfig(prev => prev ? { ...prev, [key]: !newVal } : prev);
                      showToast('Failed to save change', 'error');
                    }
                  }}
                  disabled={!canManage}
                />
              </div>
            );
          })}
        </div>

        {/* Edit form */}
        {navEditing && (
          <>
            <FieldInput
              label="Header brand text"
              value={navForm.navBrandText}
              onChange={v => setNavForm(f => ({ ...f, navBrandText: v }))}
              disabled={false}
              placeholder="Zogzila Client"
              hint="Main text next to the logo in the header"
            />
            <FieldInput
              label="Tagline badge"
              value={navForm.navTagline}
              onChange={v => setNavForm(f => ({ ...f, navTagline: v }))}
              disabled={false}
              placeholder="MARKET SIGNALS"
              hint="Small badge shown next to the logo"
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <FieldInput
                label="Button 1 label"
                value={navForm.navBtn1Label}
                onChange={v => setNavForm(f => ({ ...f, navBtn1Label: v }))}
                disabled={false}
                placeholder="Login"
              />
              <FieldInput
                label="Button 1 URL"
                value={navForm.navBtn1Url}
                onChange={v => setNavForm(f => ({ ...f, navBtn1Url: v }))}
                disabled={false}
                placeholder="/login"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <FieldInput
                label="Button 2 label"
                value={navForm.navBtn2Label}
                onChange={v => setNavForm(f => ({ ...f, navBtn2Label: v }))}
                disabled={false}
                placeholder="Register"
              />
              <FieldInput
                label="Button 2 URL"
                value={navForm.navBtn2Url}
                onChange={v => setNavForm(f => ({ ...f, navBtn2Url: v }))}
                disabled={false}
                placeholder="/register"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <FieldInput
                label="Button 3 label"
                value={navForm.navBtn3Label}
                onChange={v => setNavForm(f => ({ ...f, navBtn3Label: v }))}
                disabled={false}
                placeholder="Business Register"
              />
              <FieldInput
                label="Button 3 URL"
                value={navForm.navBtn3Url}
                onChange={v => setNavForm(f => ({ ...f, navBtn3Url: v }))}
                disabled={false}
                placeholder="/register/business"
              />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button onClick={handleNavSave} disabled={saving}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={cancelNavEdit}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        )}
      </Card>

      {/* ── About / Business Introduction ──────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <SectionTitle badge={
            <span style={{ fontSize: 11, background: '#f0fdf4', color: '#15803d', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Landing page only</span>
          }>
            🏢 About / Business Introduction
          </SectionTitle>
          {canManage && !aboutEditing && (
            <button onClick={() => setAboutEditing(true)}
              style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              ✏️ Edit
            </button>
          )}
        </div>

        {/* Show toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, marginBottom: 12,
          background: config?.showAboutSection ? '#f0fdf4' : '#f8fafc',
          border: `1.5px solid ${config?.showAboutSection ? '#bbf7d0' : '#e2e8f0'}`,
        }}>
          <span style={{ fontSize: 20 }}>{config?.showAboutSection ? '👁️' : '🚫'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show this section on the landing page</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>Appears above the hero, only on / and public /market. Hidden in the banking app and admin embeds.</div>
          </div>
          <Toggle checked={!!config?.showAboutSection} onChange={handleToggleShowAbout} disabled={!canManage} />
        </div>

        {/* Mask toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, marginBottom: 16,
          background: config?.maskAboutSection ? '#fef2f2' : '#f8fafc',
          border: `1.5px solid ${config?.maskAboutSection ? '#fecaca' : '#e2e8f0'}`,
        }}>
          <span style={{ fontSize: 20 }}>{config?.maskAboutSection ? '🙈' : '🔓'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Mask content (show placeholder while editing)</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>When masked, the section renders with blurred placeholder — real text is never sent to the browser.</div>
          </div>
          <Toggle checked={!!config?.maskAboutSection} onChange={handleToggleMaskAbout} disabled={!canManage} />
        </div>

        {/* Read-only preview */}
        {!aboutEditing && (
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Preview</div>
            {(config as any)?.aboutBadge && (
              <span style={{ display: 'inline-block', background: '#eff6ff', color: '#2563eb', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, marginBottom: 6 }}>
                {(config as any).aboutBadge}
              </span>
            )}
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', marginBottom: 6 }}>{(config as any)?.aboutTitle || '(no title)'}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, whiteSpace: 'pre-wrap' }}>{(config as any)?.aboutBody || '(no body text)'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
              {[1, 2, 3].map(n => (
                <div key={n} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: 16 }}>{(config as any)?.[`aboutHighlight${n}Icon`]}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#1e293b', marginTop: 4 }}>{(config as any)?.[`aboutHighlight${n}Title`] || `Highlight ${n}`}</div>
                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{(config as any)?.[`aboutHighlight${n}Text`] || '(no text)'}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit form */}
        {aboutEditing && (
          <>
            <FieldInput label="Badge label" value={aboutForm.aboutBadge} onChange={v => setAboutForm(f => ({ ...f, aboutBadge: v }))} disabled={false} placeholder="About Zogzila" />
            <FieldInput label="Section Title" value={aboutForm.aboutTitle} onChange={v => setAboutForm(f => ({ ...f, aboutTitle: v }))} disabled={false} placeholder="What is Zogzila?" />
            <FieldInput label="Body text" value={aboutForm.aboutBody} onChange={v => setAboutForm(f => ({ ...f, aboutBody: v }))} disabled={false} multiline placeholder="Explain what Zogzila does…" />
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', margin: '12px 0 8px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Highlight Cards</div>
            {[1, 2, 3].map(n => (
              <div key={n} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Highlight {n}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr', gap: 8, marginBottom: 8 }}>
                  <FieldInput label="Icon" value={(aboutForm as any)[`aboutHighlight${n}Icon`]} onChange={v => setAboutForm(f => ({ ...f, [`aboutHighlight${n}Icon`]: v }))} disabled={false} placeholder="📡" />
                  <FieldInput label="Title" value={(aboutForm as any)[`aboutHighlight${n}Title`]} onChange={v => setAboutForm(f => ({ ...f, [`aboutHighlight${n}Title`]: v }))} disabled={false} placeholder={`Highlight ${n} title`} />
                </div>
                <FieldInput label="Description" value={(aboutForm as any)[`aboutHighlight${n}Text`]} onChange={v => setAboutForm(f => ({ ...f, [`aboutHighlight${n}Text`]: v }))} disabled={false} placeholder="Short description…" />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button onClick={handleAboutSave} disabled={saving}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={cancelAboutEdit}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        )}
      </Card>

      {/* ── 5. Hero content ────────────────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <SectionTitle>Hero Content</SectionTitle>
          {canManage && !heroEditing && (
            <button onClick={() => setHeroEditing(true)}
              style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              ✏️ Edit
            </button>
          )}
        </div>

        {/* Live preview strip */}
        {!heroEditing && (
          <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)', borderRadius: 10, padding: '20px 24px', marginBottom: 20, color: '#fff' }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>{config?.heroTitle}</div>
            <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 12 }}>{config?.heroSubtitle}</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ background: '#fff', color: '#1e40af', borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 700 }}>{config?.heroCtaPrimary}</div>
              <div style={{ border: '1.5px solid rgba(255,255,255,0.5)', borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600 }}>{config?.heroCtaSecondary}</div>
            </div>
            {config?.announcement && (
              <div style={{ marginTop: 10, background: 'rgba(255,255,255,0.15)', borderRadius: 6, padding: '6px 12px', fontSize: 12 }}>
                📢 {config.announcement}
              </div>
            )}
          </div>
        )}

        {/* Edit form */}
        {heroEditing ? (
          <>
            <FieldInput
              label="Headline"
              value={heroForm.heroTitle}
              onChange={v => setHeroForm(f => ({ ...f, heroTitle: v }))}
              disabled={false}
              placeholder="Know When the Market is Alive"
              hint="Main heading visitors see first"
            />
            <FieldInput
              label="Subtitle"
              value={heroForm.heroSubtitle}
              onChange={v => setHeroForm(f => ({ ...f, heroSubtitle: v }))}
              disabled={false}
              multiline
              placeholder="Real-time trading signals…"
              hint="Supporting text under the headline"
            />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <FieldInput
                label="Primary CTA button label"
                value={heroForm.heroCtaPrimary}
                onChange={v => setHeroForm(f => ({ ...f, heroCtaPrimary: v }))}
                disabled={false}
                placeholder="Open Trading Account"
              />
              <FieldInput
                label="Primary CTA button URL"
                value={heroForm.heroCtaPrimaryUrl}
                onChange={v => setHeroForm(f => ({ ...f, heroCtaPrimaryUrl: v }))}
                disabled={false}
                placeholder="/register"
                hint="Internal path (e.g. /register) or full URL (https://…)"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <FieldInput
                label="Secondary CTA button label"
                value={heroForm.heroCtaSecondary}
                onChange={v => setHeroForm(f => ({ ...f, heroCtaSecondary: v }))}
                disabled={false}
                placeholder="Sign In"
              />
              <FieldInput
                label="Secondary CTA button URL"
                value={heroForm.heroCtaSecondaryUrl}
                onChange={v => setHeroForm(f => ({ ...f, heroCtaSecondaryUrl: v }))}
                disabled={false}
                placeholder="/login"
                hint="Internal path (e.g. /login) or full URL (https://…)"
              />
            </div>
            <FieldInput
              label="Announcement banner (optional — leave blank to hide)"
              value={heroForm.announcement}
              onChange={v => setHeroForm(f => ({ ...f, announcement: v }))}
              disabled={false}
              placeholder="e.g. Now supporting BTC, ETH and 30+ altcoins!"
              hint="Shown as a blue info bar at the top of the landing page"
            />

            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button onClick={handleHeroSave} disabled={saving}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={cancelHeroEdit}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          /* Read-only field list when not editing */
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { label: 'Headline', val: config?.heroTitle },
              { label: 'Announcement', val: config?.announcement || '(none)' },
              { label: 'Primary CTA label', val: config?.heroCtaPrimary },
              { label: 'Primary CTA URL', val: config?.heroCtaPrimaryUrl || '/register' },
              { label: 'Secondary CTA label', val: config?.heroCtaSecondary },
              { label: 'Secondary CTA URL', val: config?.heroCtaSecondaryUrl || '/login' },
            ].map(({ label, val }) => (
              <div key={label}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{val}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── 6. Bottom CTA Banner ───────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <SectionTitle>Bottom CTA Banner</SectionTitle>
          {canManage && !ctaBannerEditing && (
            <button onClick={() => setCtaBannerEditing(true)}
              style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              ✏️ Edit
            </button>
          )}
        </div>

        {/* Live preview strip */}
        {!ctaBannerEditing && (
          <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)', borderRadius: 10, padding: '20px 24px', marginBottom: 16, color: '#fff', textAlign: 'center' }}>
            <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 6 }}>
              {(config as any)?.ctaBannerTitle || 'Ready to Trade with an Edge?'}
            </div>
            <div style={{ fontSize: 13, opacity: 0.85 }}>
              {(config as any)?.ctaBannerSubtitle || 'Join our trading platform…'}
            </div>
          </div>
        )}

        {ctaBannerEditing ? (
          <>
            <FieldInput
              label="Banner Headline"
              value={ctaBannerForm.ctaBannerTitle}
              onChange={v => setCtaBannerForm(f => ({ ...f, ctaBannerTitle: v }))}
              disabled={false}
              placeholder="Ready to Trade with an Edge?"
              hint="The large heading shown in the blue CTA banner at the bottom of the page"
            />
            <FieldInput
              label="Banner Subtitle"
              value={ctaBannerForm.ctaBannerSubtitle}
              onChange={v => setCtaBannerForm(f => ({ ...f, ctaBannerSubtitle: v }))}
              disabled={false}
              multiline
              placeholder="Join our trading platform to access automated strategies…"
              hint="Supporting text shown under the banner headline"
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button onClick={handleCtaBannerSave} disabled={saving}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={cancelCtaBannerEdit}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { label: 'Banner Headline', val: (config as any)?.ctaBannerTitle },
              { label: 'Banner Subtitle', val: (config as any)?.ctaBannerSubtitle },
            ].map(({ label, val }) => (
              <div key={label}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{val}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── 7. Footer ──────────────────────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <SectionTitle>Footer</SectionTitle>
          {canManage && !footerEditing && (
            <button onClick={() => setFooterEditing(true)}
              style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              ✏️ Edit
            </button>
          )}
        </div>

        {/* Live preview strip */}
        {!footerEditing && (
          <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 20px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              {config?.footerCopyright || '© Zogzila · Signal data via KuCoin public API'}
            </span>
            <div style={{ display: 'flex', gap: 16 }}>
              {([1, 2, 3, 4] as const).map(n => {
                const visKey   = `footerLink${n}Visible` as keyof LandingConfig;
                const labelKey = `footerLink${n}Label`   as keyof LandingConfig;
                const visible  = (config?.[visKey]   as boolean) ?? true;
                const label    = (config?.[labelKey] as string)  || `Link ${n}`;
                return visible ? (
                  <span key={n} style={{ fontSize: 12, color: '#64748b', textDecoration: 'underline', textUnderlineOffset: 2 }}>{label}</span>
                ) : null;
              })}
            </div>
          </div>
        )}

        {/* Link visibility toggles — instant save */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: footerEditing ? 16 : 0 }}>
          {([1, 2, 3, 4] as const).map(n => {
            const visKey   = `footerLink${n}Visible` as keyof LandingConfig;
            const labelKey = `footerLink${n}Label`   as keyof LandingConfig;
            const urlKey   = `footerLink${n}Url`     as keyof LandingConfig;
            const visible  = (config?.[visKey]   as boolean) ?? true;
            const label    = (config?.[labelKey] as string)  || `Link ${n}`;
            const url      = (config?.[urlKey]   as string)  || '';
            return (
              <div key={n} style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '10px 14px', borderRadius: 10,
                background: visible ? '#f0fdf4' : '#f8fafc', border: `1.5px solid ${visible ? '#bbf7d0' : '#e2e8f0'}`,
              }}>
                <span style={{ fontSize: 18 }}>{visible ? '🔗' : '🚫'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{label}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>→ {url}</div>
                </div>
                <Toggle
                  checked={visible}
                  onChange={async () => {
                    if (!canManage || !config) return;
                    const newVal = !visible;
                    setConfig(prev => prev ? { ...prev, [visKey]: newVal } : prev);
                    try {
                      const updated = await landingAdminService.updateConfig({ [visKey]: newVal } as any);
                      setConfig(updated);
                      showToast(`Footer link "${label}" ${newVal ? 'shown' : 'hidden'}`);
                    } catch {
                      setConfig(prev => prev ? { ...prev, [visKey]: !newVal } : prev);
                      showToast('Failed to save change', 'error');
                    }
                  }}
                  disabled={!canManage}
                />
              </div>
            );
          })}
        </div>

        {/* Edit form */}
        {footerEditing && (
          <>
            <FieldInput
              label="Copyright text"
              value={footerForm.footerCopyright}
              onChange={v => setFooterForm(f => ({ ...f, footerCopyright: v }))}
              disabled={false}
              placeholder="© 2025 Zogzila · Signal data via KuCoin public API"
              hint="Displayed on the left side of the footer"
            />
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Footer links (label &amp; URL)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {([1, 2, 3, 4] as const).map(n => (
                  <React.Fragment key={n}>
                    <FieldInput
                      label={`Link ${n} label`}
                      value={(footerForm as any)[`footerLink${n}Label`]}
                      onChange={v => setFooterForm(f => ({ ...f, [`footerLink${n}Label`]: v }))}
                      disabled={false}
                      placeholder={['Login', 'Register', 'Personal', 'Business'][n - 1]}
                    />
                    <FieldInput
                      label={`Link ${n} URL`}
                      value={(footerForm as any)[`footerLink${n}Url`]}
                      onChange={v => setFooterForm(f => ({ ...f, [`footerLink${n}Url`]: v }))}
                      disabled={false}
                      placeholder={['/login', '/register', '/register/personal', '/business/register'][n - 1]}
                    />
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button onClick={handleFooterSave} disabled={saving}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={cancelFooterEdit}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        )}
      </Card>

      {/* ── 8. Proof: Market Condition Accuracy ───────────────────────── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <SectionTitle badge={
            <span style={{ fontSize: 11, background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Proof Section</span>
          }>
            📊 Market Condition Accuracy
          </SectionTitle>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {canManage && !proofRegimeEditing && (
              <button onClick={() => setProofRegimeEditing(true)}
                style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                ✏️ Edit
              </button>
            )}
          </div>
        </div>

        {/* Toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, marginBottom: 16,
          background: (config?.showProofRegime) ? '#f0fdf4' : '#f8fafc',
          border: `1.5px solid ${(config?.showProofRegime) ? '#bbf7d0' : '#e2e8f0'}`,
        }}>
          <span style={{ fontSize: 20 }}>{config?.showProofRegime ? '👁️' : '🚫'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show this section on the landing page</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>Displays Trending vs Ranging accuracy table</div>
          </div>
          <Toggle checked={!!config?.showProofRegime} onChange={() => handleToggleProof('showProofRegime')} disabled={!canManage} />
        </div>

        {/* Read-only summary */}
        {!proofRegimeEditing && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { label: 'Title',    val: (config as any)?.proofRegimeTitle    || '(default)' },
              { label: 'Accuracy', val: (config as any)?.proofRegimeAccuracy || '(not set)' },
              { label: 'Subtitle', val: (config as any)?.proofRegimeSubtitle || '(default)' },
              { label: 'Rows (JSON)', val: (config as any)?.proofRegimeRows ? `${JSON.stringify((config as any).proofRegimeRows).slice(0, 60)}…` : '(none — shows example data)' },
            ].map(({ label, val }) => (
              <div key={label}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Edit form */}
        {proofRegimeEditing && (
          <>
            <FieldInput label="Section Title" value={proofRegimeForm.proofRegimeTitle}
              onChange={v => setProofRegimeForm(f => ({ ...f, proofRegimeTitle: v }))} disabled={false}
              placeholder="Market Condition Accuracy" />
            <FieldInput label="Subtitle" value={proofRegimeForm.proofRegimeSubtitle}
              onChange={v => setProofRegimeForm(f => ({ ...f, proofRegimeSubtitle: v }))} disabled={false}
              multiline placeholder="Track when our system calls Trending vs Ranging…" />
            <FieldInput label="Accuracy Badge (e.g. 71%)" value={proofRegimeForm.proofRegimeAccuracy}
              onChange={v => setProofRegimeForm(f => ({ ...f, proofRegimeAccuracy: v }))} disabled={false}
              placeholder="71%" hint="Shown as a blue badge next to the title. Leave blank to hide." />
            <FieldInput label='Table Rows (JSON array)' value={proofRegimeForm.proofRegimeRows}
              onChange={v => setProofRegimeForm(f => ({ ...f, proofRegimeRows: v }))} disabled={false}
              multiline
              placeholder={'[{"date":"Day 1","regime":"Ranging","outcome":"Chop ✅"},{"date":"Day 2","regime":"Trending","outcome":"Strong move ✅"}]'}
              hint='JSON array of {date, regime, outcome}. Leave blank to show built-in example rows.' />
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button onClick={handleProofRegimeSave} disabled={saving}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={cancelProofRegimeEdit}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        )}
      </Card>

      {/* ── 9. Proof: Participation Filter ────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <SectionTitle badge={
            <span style={{ fontSize: 11, background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Proof Section</span>
          }>
            🔥 Participation Filter Effectiveness
          </SectionTitle>
          {canManage && !proofParticipationEditing && (
            <button onClick={() => setProofParticipationEditing(true)}
              style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              ✏️ Edit
            </button>
          )}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, marginBottom: 16,
          background: (config?.showProofParticipation) ? '#f0fdf4' : '#f8fafc',
          border: `1.5px solid ${(config?.showProofParticipation) ? '#bbf7d0' : '#e2e8f0'}`,
        }}>
          <span style={{ fontSize: 20 }}>{config?.showProofParticipation ? '👁️' : '🚫'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show this section on the landing page</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>HOT vs COOL coin avg move comparison</div>
          </div>
          <Toggle checked={!!config?.showProofParticipation} onChange={() => handleToggleProof('showProofParticipation')} disabled={!canManage} />
        </div>

        {!proofParticipationEditing && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { label: 'Title',          val: (config as any)?.proofParticipationTitle    || '(default)' },
              { label: 'HOT coins avg',  val: (config as any)?.proofHotAvg                || '(not set)' },
              { label: 'Subtitle',       val: (config as any)?.proofParticipationSubtitle || '(default)' },
              { label: 'COOL coins avg', val: (config as any)?.proofCoolAvg               || '(not set)' },
            ].map(({ label, val }) => (
              <div key={label}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{val}</div>
              </div>
            ))}
          </div>
        )}

        {proofParticipationEditing && (
          <>
            <FieldInput label="Section Title" value={proofParticipationForm.proofParticipationTitle}
              onChange={v => setProofParticipationForm(f => ({ ...f, proofParticipationTitle: v }))} disabled={false}
              placeholder="Participation Filter Effectiveness" />
            <FieldInput label="Subtitle" value={proofParticipationForm.proofParticipationSubtitle}
              onChange={v => setProofParticipationForm(f => ({ ...f, proofParticipationSubtitle: v }))} disabled={false}
              multiline placeholder="HOT coins vs COOL coins — where real momentum lives" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <FieldInput label="HOT Coins Avg Move" value={proofParticipationForm.proofHotAvg}
                onChange={v => setProofParticipationForm(f => ({ ...f, proofHotAvg: v }))} disabled={false}
                placeholder="+12%" hint="e.g. +12%. Leave blank to hide the stat card." />
              <FieldInput label="COOL Coins Avg Move" value={proofParticipationForm.proofCoolAvg}
                onChange={v => setProofParticipationForm(f => ({ ...f, proofCoolAvg: v }))} disabled={false}
                placeholder="+2%" hint="e.g. +2% or flat" />
            </div>
            <FieldInput label="Table Rows (JSON array)" value={proofParticipationForm.proofParticipationRows}
              onChange={v => setProofParticipationForm(f => ({ ...f, proofParticipationRows: v }))} disabled={false}
              multiline
              placeholder={'[{"label":"HOT coins avg move","result":"+12%"},{"label":"COOL coins avg move","result":"+2%"}]'}
              hint='JSON array of {label, result}. Leave blank to hide the detail table.' />
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button onClick={handleProofParticipationSave} disabled={saving}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={cancelProofParticipationEdit}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        )}
      </Card>

      {/* ── 10. Proof: Signal Quality ──────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <SectionTitle badge={
            <span style={{ fontSize: 11, background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Proof Section</span>
          }>
            🎯 Signal Quality
          </SectionTitle>
          {canManage && !proofSignalsEditing && (
            <button onClick={() => setProofSignalsEditing(true)}
              style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              ✏️ Edit
            </button>
          )}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, marginBottom: 16,
          background: (config?.showProofSignals) ? '#f0fdf4' : '#f8fafc',
          border: `1.5px solid ${(config?.showProofSignals) ? '#bbf7d0' : '#e2e8f0'}`,
        }}>
          <span style={{ fontSize: 20 }}>{config?.showProofSignals ? '👁️' : '🚫'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show this section on the landing page</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>Win rate, avg move and max drawdown over tracked windows</div>
          </div>
          <Toggle checked={!!config?.showProofSignals} onChange={() => handleToggleProof('showProofSignals')} disabled={!canManage} />
        </div>

        {!proofSignalsEditing && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            {[
              { label: 'Win Rate',     val: (config as any)?.proofSignalWinRate  || '(not set)' },
              { label: 'Avg Move',     val: (config as any)?.proofSignalAvgMove  || '(not set)' },
              { label: 'Max Drawdown', val: (config as any)?.proofSignalMaxDD    || '(not set)' },
            ].map(({ label, val }) => (
              <div key={label} style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b' }}>{val}</div>
              </div>
            ))}
          </div>
        )}

        {proofSignalsEditing && (
          <>
            <FieldInput label="Section Title" value={proofSignalsForm.proofSignalsTitle}
              onChange={v => setProofSignalsForm(f => ({ ...f, proofSignalsTitle: v }))} disabled={false}
              placeholder="Signal Quality" />
            <FieldInput label="Subtitle" value={proofSignalsForm.proofSignalsSubtitle}
              onChange={v => setProofSignalsForm(f => ({ ...f, proofSignalsSubtitle: v }))} disabled={false}
              multiline placeholder="Bullish/Bearish bias tracked over a fixed window…" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <FieldInput label="Win Rate" value={proofSignalsForm.proofSignalWinRate}
                onChange={v => setProofSignalsForm(f => ({ ...f, proofSignalWinRate: v }))} disabled={false}
                placeholder="68%" hint="e.g. 68%" />
              <FieldInput label="Avg Move" value={proofSignalsForm.proofSignalAvgMove}
                onChange={v => setProofSignalsForm(f => ({ ...f, proofSignalAvgMove: v }))} disabled={false}
                placeholder="+8.4%" hint="e.g. +8.4%" />
              <FieldInput label="Max Drawdown" value={proofSignalsForm.proofSignalMaxDD}
                onChange={v => setProofSignalsForm(f => ({ ...f, proofSignalMaxDD: v }))} disabled={false}
                placeholder="-3.1%" hint="e.g. -3.1%" />
            </div>
            <FieldInput label="Signal Rows (JSON array)" value={proofSignalsForm.proofSignalRows}
              onChange={v => setProofSignalsForm(f => ({ ...f, proofSignalRows: v }))} disabled={false}
              multiline
              placeholder={'[{"coin":"BTC","bias":"Bullish","window":"24h","outcome":"+6.2%"},{"coin":"ETH","bias":"Bearish","window":"48h","outcome":"-4.1%"}]'}
              hint='JSON array of {coin, bias, window, outcome}. Leave blank to hide the detail table.' />
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button onClick={handleProofSignalsSave} disabled={saving}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={cancelProofSignalsEdit}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        )}
      </Card>

      {/* ── 11. Proof: Winning Trades ───────────────────────────────────── */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <SectionTitle badge={
            <span style={{ fontSize: 11, background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Proof Section</span>
          }>
            🏆 Winning Trades
          </SectionTitle>
          {canManage && !proofWinningTradesEditing && (
            <button onClick={() => setProofWinningTradesEditing(true)}
              style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              ✏️ Edit
            </button>
          )}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, marginBottom: 16,
          background: (config?.showProofWinningTrades) ? '#f0fdf4' : '#f8fafc',
          border: `1.5px solid ${(config?.showProofWinningTrades) ? '#bbf7d0' : '#e2e8f0'}`,
        }}>
          <span style={{ fontSize: 20 }}>{config?.showProofWinningTrades ? '👁️' : '🚫'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show this section on the landing page</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>Top profitable trades from active bots (20 rows, paginated 5 per page)</div>
          </div>
          <Toggle checked={!!config?.showProofWinningTrades} onChange={() => handleToggleProof('showProofWinningTrades')} disabled={!canManage} />
        </div>

        {/* Visibility mode — segmented control: Masked / Top 2 / Top 5 / All */}
        {(() => {
          const masked = !!config?.maskWinningTrades;
          const limit  = config?.winningTradesDisplayLimit ?? 0;
          const current: 'masked' | 0 | 2 | 5 = masked ? 'masked' : (limit as 0 | 2 | 5);
          const opts: { value: 'masked' | 0 | 2 | 5; icon: string; label: string; desc: string }[] = [
            { value: 'masked', icon: '🙈', label: 'Masked',  desc: 'Blurred placeholder rows' },
            { value: 2,        icon: '2️⃣',  label: 'Top 2',  desc: 'Show only 2 records'      },
            { value: 5,        icon: '5️⃣',  label: 'Top 5',  desc: 'Show only 5 records'      },
            { value: 0,        icon: '📋',  label: 'All',    desc: 'Show all rows (up to 20)' },
          ];
          return (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                Data visibility
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
                {opts.map(opt => {
                  const active = opt.value === current;
                  return (
                    <button
                      key={String(opt.value)}
                      onClick={() => canManage && handleSetWinningTradesVisibility(opt.value)}
                      disabled={!canManage}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                        padding: '10px 6px', borderRadius: 10, border: `2px solid ${active ? '#2563eb' : '#e2e8f0'}`,
                        background: active ? '#eff6ff' : '#f8fafc',
                        cursor: canManage ? 'pointer' : 'not-allowed',
                        opacity: canManage ? 1 : 0.5, transition: 'all 0.15s',
                      }}
                    >
                      <span style={{ fontSize: 20 }}>{opt.icon}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: active ? '#2563eb' : '#475569' }}>{opt.label}</span>
                      <span style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', lineHeight: 1.3 }}>{opt.desc}</span>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6, lineHeight: 1.5 }}>
                {current === 'masked'
                  ? '🙈 Section is visible but shows blurred placeholder rows — real trades are never sent to the browser.'
                  : current === 0
                    ? '📋 All available trades (up to 20) are shown with real data.'
                    : `${current === 2 ? '2️⃣' : '5️⃣'} Only the top ${current} most recent profitable trades are shown.`}
              </div>
            </div>
          );
        })()}

        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, marginBottom: 10,
          background: (config as any)?.showProofLosingTrades ? '#fef2f2' : '#f8fafc',
          border: `1.5px solid ${(config as any)?.showProofLosingTrades ? '#fecaca' : '#e2e8f0'}`,
        }}>
          <span style={{ fontSize: 20 }}>{(config as any)?.showProofLosingTrades ? '📉' : '🏆'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Include losing trades</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
              When on, recent closes with negative PNL appear in the table (mixed with wins). Default off for public marketing.
            </div>
          </div>
          <Toggle
            checked={!!(config as any)?.showProofLosingTrades}
            onChange={() => handleToggleWinningTradesFlag('showProofLosingTrades')}
            disabled={!canManage}
          />
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, marginBottom: 10,
          background: (config as any)?.showProofWinningTradesDailyPnl ? '#f0fdf4' : '#f8fafc',
          border: `1.5px solid ${(config as any)?.showProofWinningTradesDailyPnl ? '#bbf7d0' : '#e2e8f0'}`,
        }}>
          <span style={{ fontSize: 20 }}>📅</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show Daily PNL (all bots) column</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
              UTC-day realized PNL summed across all active bots on that close date. Trade PNL column always shown.
            </div>
          </div>
          <Toggle
            checked={(config as any)?.showProofWinningTradesDailyPnl !== false}
            onChange={() => handleToggleWinningTradesFlag('showProofWinningTradesDailyPnl')}
            disabled={!canManage}
          />
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 10, marginBottom: 16,
          background: (config as any)?.showProofWinningTradesDailyPnlPerStrategy ? '#f0fdf4' : '#f8fafc',
          border: `1.5px solid ${(config as any)?.showProofWinningTradesDailyPnlPerStrategy ? '#bbf7d0' : '#e2e8f0'}`,
        }}>
          <span style={{ fontSize: 20 }}>📊</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show Daily PNL (per strategy) column</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
              UTC-day realized PNL for the row&apos;s strategy only — matches the Strategy column. Default off.
            </div>
          </div>
          <Toggle
            checked={!!(config as any)?.showProofWinningTradesDailyPnlPerStrategy}
            onChange={() => handleToggleWinningTradesFlag('showProofWinningTradesDailyPnlPerStrategy')}
            disabled={!canManage}
          />
        </div>

        {!proofWinningTradesEditing && (
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
              Section Title
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>{(config as any)?.proofWinningTradesTitle || 'Winning Trades'}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{(config as any)?.proofWinningTradesSubtitle || '(not set)'}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
              Strategy name column: <strong>{(config as any)?.showProofWinningTradesStrategyName ? 'Shown' : 'Hidden'}</strong>
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              Losing trades: <strong>{(config as any)?.showProofLosingTrades ? 'Included' : 'Wins only'}</strong>
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              Daily PNL (all bots): <strong>{(config as any)?.showProofWinningTradesDailyPnl !== false ? 'Shown' : 'Hidden'}</strong>
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              Daily PNL (per strategy): <strong>{(config as any)?.showProofWinningTradesDailyPnlPerStrategy ? 'Shown' : 'Hidden'}</strong>
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              Data visibility: <strong>
                {(config as any)?.maskWinningTrades
                  ? '🙈 Masked'
                  : (config as any)?.winningTradesDisplayLimit === 2
                    ? '2️⃣ Top 2 rows'
                    : (config as any)?.winningTradesDisplayLimit === 5
                      ? '5️⃣ Top 5 rows'
                      : '📋 All rows'}
              </strong>
            </div>
          </div>
        )}

        {proofWinningTradesEditing && (
          <>
            <FieldInput label="Section Title" value={proofWinningTradesForm.proofWinningTradesTitle}
              onChange={v => setProofWinningTradesForm(f => ({ ...f, proofWinningTradesTitle: v }))} disabled={false}
              placeholder="Winning Trades" />
            <FieldInput label="Subtitle" value={proofWinningTradesForm.proofWinningTradesSubtitle}
              onChange={v => setProofWinningTradesForm(f => ({ ...f, proofWinningTradesSubtitle: v }))} disabled={false}
              multiline
              placeholder="Most recent profitable closes from active strategy bots (same live exchange data as bot analytics)" />
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
              borderRadius: 10, marginBottom: 12, background: '#f8fafc', border: '1.5px solid #e2e8f0',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>Show strategy name column</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>
                  Hide this if you want to display winners without revealing bot strategy names.
                </div>
              </div>
              <Toggle
                checked={!!proofWinningTradesForm.showProofWinningTradesStrategyName}
                onChange={() => setProofWinningTradesForm(f => ({ ...f, showProofWinningTradesStrategyName: !f.showProofWinningTradesStrategyName }))}
                disabled={false}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button onClick={handleProofWinningTradesSave} disabled={saving}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 28px', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={cancelProofWinningTradesEdit}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        )}
      </Card>

      {/* ── 7. Paid Banners (dynamic) ─────────────────────────────────── */}
      <Card>
        <SectionTitle badge={<span style={{ fontSize: 11, background: '#ede9fe', color: '#5b21b6', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>Paid Banners</span>}>
          Sidebar Banners
        </SectionTitle>
        <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px', lineHeight: 1.6 }}>
          Add as many banners as you need on the Left or Right side of the landing page. Set width/height (px) or leave blank for auto. Hidden on the retail Market Signals page.
        </p>

        {/* Add buttons */}
        {canManage && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
            <button onClick={() => handleAddBanner('LEFT')} disabled={bannerSaving === 'new'}
              style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: bannerSaving === 'new' ? 0.6 : 1 }}>
              + Add Left Banner
            </button>
            <button onClick={() => handleAddBanner('RIGHT')} disabled={bannerSaving === 'new'}
              style={{ background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: bannerSaving === 'new' ? 0.6 : 1 }}>
              + Add Right Banner
            </button>
          </div>
        )}

        {banners.length === 0 && (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8', fontSize: 13 }}>
            No banners yet. Click "Add Left Banner" or "Add Right Banner" to create one.
          </div>
        )}

        {(['LEFT', 'RIGHT'] as const).map(side => {
          const sideBanners = banners.filter(b => b.side === side);
          if (sideBanners.length === 0) return null;
          return (
            <div key={side} style={{ marginBottom: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#7c3aed', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                {side === 'LEFT' ? '◀' : '▶'} {side} BANNERS
              </div>
              {sideBanners.map(b => {
                const isSavingThis = bannerSaving === b.id;
                return (
                  <div key={b.id} style={{ marginBottom: 16, borderRadius: 12, border: `1.5px solid ${b.visible ? '#c4b5fd' : '#e2e8f0'}`, overflow: 'hidden' }}>
                    {/* Header row */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                      background: b.visible ? '#f5f3ff' : '#f8fafc',
                      borderBottom: '1px solid #e2e8f0',
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
                          {b.title || `(${side} Banner #${b.id})`}
                          {isSavingThis && <span style={{ fontSize: 11, color: '#6366f1', marginLeft: 8 }}>saving…</span>}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          {b.visible ? (b.masked ? '🔒 Masked placeholder' : '✅ Live & visible') : '🚫 Hidden'}
                          {b.widthPx ? ` · ${b.widthPx}px wide` : ''}
                          {b.heightPx ? ` · ${b.heightPx}px tall` : ''}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, color: '#64748b' }}>Show</span>
                        <Toggle checked={b.visible} onChange={() => handleUpdateBanner(b.id!, 'visible', !b.visible)} disabled={!canManage} />
                        {b.visible && (<>
                          <span style={{ fontSize: 11, color: '#64748b', marginLeft: 4 }}>Mask</span>
                          <Toggle checked={b.masked} onChange={() => handleUpdateBanner(b.id!, 'masked', !b.masked)} disabled={!canManage} />
                        </>)}
                        {canManage && (
                          <button onClick={() => handleDeleteBanner(b.id!)}
                            style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginLeft: 6 }}>
                            🗑
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Edit fields */}
                    <div style={{ padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' }}>
                      {([
                        { f: 'badge',      label: 'Badge',         ph: 'SPONSORED' },
                        { f: 'bgColor',    label: 'BG color',      ph: '#f0f9ff' },
                        { f: 'title',      label: 'Title',         ph: 'Headline', wide: true },
                        { f: 'subtitle',   label: 'Subtitle',      ph: 'Short description', wide: true },
                        { f: 'imageUrl',   label: 'Image URL',     ph: 'https://…/img.png  or  /assets/banners/banner-hero-1200x400.png', wide: true },
                        { f: 'linkUrl',    label: 'Link URL',      ph: 'https://…', wide: true },
                        { f: 'linkLabel',  label: 'Button label',  ph: 'Learn More' },
                        { f: 'widthPx',    label: 'Width (px)',    ph: 'auto', numeric: true },
                        { f: 'heightPx',   label: 'Height (px)',   ph: 'auto', numeric: true },
                      ] as { f: keyof BannerRow; label: string; ph: string; wide?: boolean; numeric?: boolean }[]).map(({ f, label, ph, wide, numeric }) => (
                        <div key={String(f)} style={{ gridColumn: wide ? '1 / -1' : undefined }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 3 }}>{label}</div>
                          {f === 'imageUrl' ? (
                            <BannerImageDropZone
                              value={(b as any).imageUrl ?? ''}
                              disabled={!canManage}
                              onChange={(url: string) => {
                                setBanners(prev => prev.map(x => x.id === b.id ? { ...x, imageUrl: url } : x));
                                handleUpdateBanner(b.id!, 'imageUrl', url);
                              }}
                            />
                          ) : (
                            <input
                              type={numeric ? 'number' : 'text'}
                              value={(b as any)[f] ?? ''}
                              placeholder={ph}
                              disabled={!canManage}
                              onBlur={e => {
                                const raw = e.target.value;
                                const val = numeric ? (raw === '' ? null : Number(raw)) : raw;
                                handleUpdateBanner(b.id!, f, val);
                              }}
                              onChange={e => setBanners(prev => prev.map(x => x.id === b.id ? { ...x, [f]: e.target.value } : x))}
                              style={{
                                width: '100%', boxSizing: 'border-box',
                                padding: '6px 9px', borderRadius: 7, border: '1px solid #e2e8f0',
                                fontSize: 13, color: '#1e293b', background: canManage ? '#fff' : '#f8fafc',
                              }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </Card>

      {/* ── 8. Quick help ─────────────────────────────────────────────────── */}
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '14px 20px', fontSize: 13, color: '#92400e' }}>
        <strong>How editing works:</strong>
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, lineHeight: 1.8 }}>
          <li><strong>Section toggles</strong> save instantly — no Save button needed.</li>
          <li><strong>Hero content</strong> — click ✏️ Edit, make your changes, then click Save Changes.</li>
          <li><strong>Preview</strong> — use{' '}
            <a href="/admin/dashboard?tab=marketSignals" style={{ color: '#b45309', fontWeight: 700 }}>/admin/dashboard?tab=marketSignals</a>{' '}
            for admin-layout preview (header/sidebar), while <strong>/</strong> is the public retail landing view.</li>
          <li><strong>Image-only banner</strong> — paste an image URL, leave Title/Subtitle/Badge empty, and the banner will show only the full image (clickable if Link URL is set).</li>
          <li><strong>Drag &amp; drop / browse</strong> — drop a file or click to browse; it uploads to the server and saves the hosted URL automatically. If upload fails, use "Paste URL" instead.</li>
          <li><strong>Local images</strong> — images in <code>/assets/banners/</code> can be referenced as <code>/assets/banners/banner-hero-1200x400.png</code> etc.</li>
          <li><strong>External images</strong> — paste any public <code>https://…</code> image URL.</li>
          <li><strong>Publish / Unpublish</strong> — controls whether <code style={{ background: '#fef3c7', padding: '1px 5px', borderRadius: 4 }}>/</code> shows the landing page or redirects to login.</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminLandingPageManager;
